var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as Sentry from '@sentry/browser';
import * as turf from '@turf/turf';
import axios from 'axios';
import { EventEmitter } from 'events';
import _, { floor } from 'lodash';
import * as THREE from 'three';
import { Model3D } from './Model3D';
import { _get, projectedUnitsPerMeter, uuid } from './utils';
import ThreeJSTextLayer from './ThreeJSTextLayer';
import mapboxgl from 'mapbox-gl';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import defaultFont from '../static/Montserrat_SemiBold_Regular';
import mddFont from '../static/Neutraface_2_Text_Demi_Regular';
import Pathfinder from 'geojson-path-finder';
import { fetchMapboxDataset } from "./mapbox";
import { MapboxRouter } from '@smartlinkdev/routing';
const accessKey = 'pk.eyJ1Ijoic21hcnRsaW5rZGV2IiwiYSI6ImNqc3Z2dHU5bjA4dWE0NHBqa3U4dnE0aTIifQ.Cctjg3CDUrjXHSBL_itBRA';
const NUM_FT_IN_MILE = 5280;
class MapRoutingError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MapRoutingError';
    }
}
;
export default class SkyOSMap extends EventEmitter {
    constructor(settings, debug, browserSettings, additionalDebug) {
        var _a, _b;
        super();
        this.containerId = 'SkyOSMapContainer';
        this.routes = [];
        this.logTransports = [];
        this.currentlyRouting = false;
        this.routingTimeouts = [];
        this.handicapMode = false;
        this.lastHeadings = [];
        this.logoPosition = 'top-left';
        this.routingLines = [];
        Sentry.init({
            dsn: 'https://90af6df91601478db1edcd259bb609b8@o133726.ingest.sentry.io/5606856',
            enableTracing: false,
            integrations: [],
            sampleRate: 0.1,
        });
        this.settings = settings;
        this.screenId = ((_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.screen) === null || _b === void 0 ? void 0 : _b.id) || 0;
        this.floors = [];
        this.debug = debug || true;
        this.browserSettings = browserSettings || {
            disableClickRouting: false,
            disableYouAreHere: false,
            directionalYouAreHere: false,
            snapToLine: false
        };
        this.debugDisable3dObjects = false;
        this.debugDisable3dNames = false;
        this.debugLevel = 'error';
        if (browserSettings && browserSettings.clientId) {
            this.clientId = browserSettings.clientId;
        }
        if (browserSettings && browserSettings.sessionId) {
            this.sessionId = browserSettings.sessionId;
        }
        if (settings.logoPosition) {
            this.logoPosition = settings.logoPosition;
        }
        const screenId = _.get(this.settings, 'screen.id', null);
        const venueId = _.get(this.settings, 'venue.id', null);
        const defaultMapLoadData = {
            type: this.isMobile() ? 'mobile' : 'kiosk',
            screen_id: screenId,
            venue_id: venueId,
            client_id: this.getIdString(this.clientId),
            session_id: this.getIdString(this.sessionId),
        };
        this.mapLoadData = _.merge(defaultMapLoadData, this.browserSettings.mapLoadData);
        if (additionalDebug) {
            this.debugLevel = additionalDebug.logLevel || 'error';
            this.debugDisable3dObjects = additionalDebug.disable3dObjects || false;
            this.debugDisable3dNames = additionalDebug.disable3dNames || false;
            if (this.debugLevel !== 'error' || this.debugDisable3dObjects || this.debugDisable3dNames) {
                console.log('Map Debug Config', {
                    debugLevel: this.debugLevel,
                    disable3dObjects: this.debugDisable3dObjects,
                    disable3dNames: this.debugDisable3dNames,
                });
            }
        }
    }
    render(renderOpts) {
        this.log('debug', 'render: called with opts: ', renderOpts);
        return Promise.resolve();
    }
    updateUserPositionAndMarker(position, useHeading) {
        this.settings.currentPosition = position;
        if (position.coords && position.coords.heading && useHeading) {
            if (!this.currentlyRouting && window.mapInMemory) {
                window.mapInMemory.rotateTo(position.coords.heading, { duration: 500 });
            }
        }
        this.displayYouAreHere();
    }
    addLogTransport(logger) {
        this.logTransports.push(logger);
    }
    log(fn, ...args) {
        if (!this.debug) {
            return;
        }
        switch (fn) {
            case 'error': {
                this.logTransports.forEach(transport => {
                    transport.error(...args);
                });
                break;
            }
            case 'warn': {
                if (this.debugLevel !== 'error') {
                    this.logTransports.forEach(transport => {
                        transport.log('WARNING', ...args);
                    });
                }
                break;
            }
            case 'info': {
                if (this.debugLevel !== 'error' && this.debugLevel !== 'warn') {
                    this.logTransports.forEach(transport => {
                        transport.log('INFO', ...args);
                    });
                }
                break;
            }
            case 'verbose': {
                if (this.debugLevel !== 'error' && this.debugLevel !== 'warn' && this.debugLevel !== 'info') {
                    this.logTransports.forEach(transport => {
                        transport.log('VERBOSE', ...args);
                    });
                }
                break;
            }
            case 'debug': {
                if (this.debugLevel === 'debug' || this.debugLevel === 'silly') {
                    this.logTransports.forEach(transport => {
                        transport.log('DEBUG', ...args);
                    });
                }
                break;
            }
            case 'silly': {
                if (this.debugLevel === 'silly') {
                    this.logTransports.forEach(transport => {
                        transport.log('SILLY', ...args);
                    });
                }
                break;
            }
            default: {
                this.logTransports.forEach(transport => {
                    transport.log(...args);
                });
                break;
            }
        }
    }
    appendRemoteScriptToHead() {
        this.log('debug', 'appendRemoteScriptToHead called');
        const mapboxGlVersionUrl = 'https://api.tiles.mapbox.com/mapbox-gl-js/v2.8.2';
        const mapboxStyleTag = document.getElementById('mapboxMapStyle');
        if (!mapboxStyleTag) {
            this.log('debug', 'appendRemoteScriptToHead: appending mapbox css to header');
            const mbStyle = document.createElement('LINK');
            mbStyle.setAttribute('href', `${mapboxGlVersionUrl}/mapbox-gl.css`);
            mbStyle.setAttribute('rel', 'stylesheet');
            mbStyle.setAttribute('id', 'mapboxMapStyle');
            document.head.appendChild(mbStyle);
        }
        this.log('debug', 'appendRemoteScriptToHead: emit map is ready');
        this.emit('jsReady');
    }
    displayYouAreHere() {
        const el = document.getElementById('youarehere');
        this.log('debug', 'displayYouAreHere: called');
        if (this.browserSettings && this.browserSettings.disableYouAreHere) {
            if (el) {
                el.style.display = 'none';
            }
            return;
        }
        let display = 'none';
        if (this.currentFloorId === this.getDefaultFloorId()) {
            display = 'block';
        }
        if (this.settings.currentPosition) {
            display = 'block';
        }
        if (el) {
            this.log('debug', 'displayYouAreHere: using existing youarehere, changing display to: ', display);
            el.style.display = display;
            if (this.settings.currentPosition && this.settings.currentPosition.coords && window.youAreHerePin) {
                this.recalculateRoute();
                const insideGeofence = this.insideGeofence();
                this.log('debug', 'Update you are here position marker', insideGeofence);
                if (insideGeofence) {
                    this.log('debug', 'inside geofence');
                    let updatePosition = true;
                    if (this.settings.lastPosition) {
                        if (this.settings.currentPosition.coords.accuracy) {
                            const oldPos = [this.settings.lastPosition.lng, this.settings.lastPosition.lat];
                            const newPos = [
                                this.settings.currentPosition.coords.longitude,
                                this.settings.currentPosition.coords.latitude
                            ];
                            const distance = turf.distance(newPos, oldPos) * 1000;
                            this.log('debug', 'distance between points', distance);
                            if (distance < this.settings.currentPosition.coords.accuracy) {
                                updatePosition = false;
                            }
                        }
                    }
                    if (updatePosition) {
                        this.settings.lastPosition = {
                            lng: this.settings.currentPosition.coords.longitude,
                            lat: this.settings.currentPosition.coords.latitude
                        };
                        let updateCoords = {
                            lat: this.settings.currentPosition.coords.latitude,
                            lng: this.settings.currentPosition.coords.longitude
                        };
                        if (this.currentlyRouting &&
                            this.browserSettings.snapToLine &&
                            this.settings.currentPosition.coords.accuracy) {
                            updateCoords = this.snapToLine();
                        }
                        if (window.youAreHerePin) {
                            window.youAreHerePin.setLngLat({
                                lat: updateCoords.lat,
                                lng: updateCoords.lng
                            });
                        }
                        if (window.mapInMemory) {
                            window.mapInMemory.setCenter({
                                lat: updateCoords.lat,
                                lon: updateCoords.lng
                            });
                        }
                        this.arriveDestination();
                    }
                    if (this.settings.currentPosition.coords.accuracy && !this.currentlyRouting) {
                        this.displayYouAreHereAccuracy(this.settings.currentPosition.coords);
                    }
                    else {
                        const verifyLayer = window.mapInMemory.getLayer('youAreHerePinAccuracy');
                        if (verifyLayer) {
                            window.mapInMemory.setLayoutProperty('youAreHerePinAccuracy', 'visibility', 'none');
                        }
                    }
                    if (this.currentlyRouting) {
                        this.updateMapBearingForRouting();
                    }
                    if (this.browserSettings.directionalYouAreHere && this.settings.currentPosition.coords.heading !== null) {
                        this.updateUserDirection();
                    }
                }
                else {
                    this.log('debug', 'outside geofence');
                    el.style.display = 'none';
                }
            }
            return;
        }
        const customMarker = _get(this.settings, 'venue.settings.mapboxSettings.youAreHere', '/static/youareheredefault.png');
        this.log('debug', 'displayYouAreHere: generating new youarehere element, display: ', display);
        const youAreHereEl = document.createElement('div');
        youAreHereEl.id = 'youarehere';
        youAreHereEl.className = 'youarehere';
        youAreHereEl.style.zIndex = '100';
        let anchor = 'bottom';
        let pitchAlignment = 'auto';
        let rotationAlignment = 'auto';
        if (this.browserSettings.directionalYouAreHere) {
            youAreHereEl.style.backgroundImage =
                'url("https://smartlink-kiosk-cms-ads.s3.us-east-2.amazonaws.com/here_blank2.png")';
            youAreHereEl.style.backgroundSize = 'contain';
            youAreHereEl.style.width = '60px';
            youAreHereEl.style.height = '60px';
            youAreHereEl.style.position = 'relative';
            anchor = 'center';
            pitchAlignment = 'map';
            rotationAlignment = 'map';
        }
        else {
            youAreHereEl.style.height = '90px';
            youAreHereEl.style.width = '120px';
            youAreHereEl.style.marginBottom = '100px';
            youAreHereEl.style.backgroundImage = `url(${customMarker})`;
            youAreHereEl.style.backgroundRepeat = 'no-repeat';
            youAreHereEl.style.backgroundSize = 'contain';
            youAreHereEl.style.backgroundPosition = 'center';
            youAreHereEl.style.display = display;
        }
        let lng, lat;
        if (this.settings.currentPosition && this.settings.currentPosition.coords) {
            this.log('debug', 'User has a current position');
            const insideGeofence = this.insideGeofence();
            if (insideGeofence) {
                this.log('debug', 'currentpos', this.settings.currentPosition);
                lng = this.settings.currentPosition.coords.longitude.toString();
                lat = this.settings.currentPosition.coords.latitude.toString();
            }
            else {
                this.log('debug', 'dont display marker as it is outside geofence');
                return;
            }
        }
        if (lng === undefined) {
            lng = this.getKioskLocation()[0].toString();
            lat = this.getKioskLocation()[1].toString();
        }
        this.setMarker({
            coord: { lng, lat },
            htmlElement: youAreHereEl,
            html: '',
            youAreHere: true,
            anchor,
            pitchAlignment,
            rotationAlignment
        });
        const heading = _.get(this.settings, 'currentPosition.coords.heading');
        if (this.browserSettings.directionalYouAreHere && heading) {
            this.updateUserDirection();
        }
    }
    getRouteBearing(path) {
        var _a, _b, _c, _d, _e, _f;
        let closestIndex = 0;
        let closestDistance = 100000;
        const currentPoint = [
            ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
            ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
        ];
        path.geometry.coordinates.forEach((coord, index) => {
            const distance = turf.distance(coord, currentPoint);
            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = index;
            }
        });
        let lookIndex = closestIndex + 10;
        if (!path.geometry.coordinates[lookIndex]) {
            lookIndex = path.geometry.coordinates.length - 1;
        }
        return turf.bearing(currentPoint, path.geometry.coordinates[lookIndex]);
    }
    updateMapBearingForRouting() {
        if (this.currentRoutingPath && window.mapInMemory) {
            const bearing = this.getRouteBearing(this.currentRoutingPath);
            this.log('debug', 'attempting to put line to the top', bearing);
            window.mapInMemory.rotateTo(bearing, { duration: 500 });
        }
    }
    hideYouAreHereAccuracy() {
        if (!window.mapInMemory) {
            return;
        }
        const layer = window.mapInMemory.getLayer('youAreHerePinAccuracy');
        if (layer) {
            window.mapInMemory.setLayoutProperty('youAreHerePinAccuracy', 'visibility', 'none');
        }
    }
    showYouAreHereAccuracy() {
        if (!window.mapInMemory) {
            return;
        }
        const layer = window.mapInMemory.getLayer('youAreHerePinAccuracy');
        if (layer) {
            window.mapInMemory.setLayoutProperty('youAreHerePinAccuracy', 'visibility', 'visible');
        }
    }
    displayYouAreHereAccuracy(position) {
        if (!window.mapInMemory || !this.settings.lastPosition) {
            return;
        }
        const source = window.mapInMemory.getSource('youAreHerePinAccuracy');
        let circleSize = position.accuracy;
        if (circleSize > 200) {
            circleSize = 200;
        }
        const circle = turf.circle([this.settings.lastPosition.lng, this.settings.lastPosition.lat], circleSize / 1000);
        if (!source) {
            window.mapInMemory
                .addSource('youAreHerePinAccuracy', {
                type: 'geojson',
                data: circle
            })
                .addLayer({
                id: 'youAreHerePinAccuracy',
                type: 'fill',
                source: 'youAreHerePinAccuracy',
                paint: {
                    'fill-color': '#0b84fe',
                    'fill-opacity': 0.4
                }
            });
        }
        else {
            source.setData(circle);
            window.mapInMemory.setLayoutProperty('youAreHerePinAccuracy', 'visibility', this.currentlyRouting ? 'none' : 'visible');
        }
    }
    updateUserDirection() {
        var _a, _b, _c;
        const currentHeading = (_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.heading;
        if (currentHeading === null || currentHeading === undefined || !this.insideGeofence()) {
            return;
        }
        this.lastHeadings.push(currentHeading);
        if (this.lastHeadings.length > 10) {
            this.lastHeadings = _.takeRight(this.lastHeadings, 10);
        }
        const newHeading = _.mean(this.lastHeadings);
        this.log('debug', `actual heading ${currentHeading} smoothed ${newHeading}`);
        const el = document.getElementById('youarehere');
        if (el) {
            el.style.backgroundImage = 'url("https://smartlink-kiosk-cms-ads.s3.us-east-2.amazonaws.com/here_blank2.png")';
            const arrow = document.getElementById('youarehereholder');
            if (!arrow) {
                const holder = document.createElement('div');
                holder.id = 'youarehereholder';
                holder.style.width = '60px';
                holder.style.height = '60px';
                holder.style.position = 'absolute';
                holder.style.backgroundImage = 'url("https://smartlink-kiosk-cms-ads.s3.us-east-2.amazonaws.com/here_arrow2.png")';
                holder.style.backgroundSize = 'contain';
                holder.style.top = '0';
                holder.style.left = '0';
                holder.style.transform = `rotate(${newHeading}deg)`;
                el.appendChild(holder);
            }
            else {
                arrow.style.transform = `rotate(${newHeading}deg)`;
            }
        }
    }
    changeFloor(opts) {
        this.log('debug', 'changeFloor: called with opts: ', opts);
        if (this.settings.disableAutoSwitchFloors && this.routingLines && this.routingLines.length > 0) {
            const found = this.routingLines.findIndex((rl) => rl.floor.floor.id === opts.newFloor.id);
            if (found !== -1) {
                this.displaySingleRoute(found);
            }
        }
        if (opts.newFloor.id === this.currentFloorId) {
            this.log('debug', 'no need to change floor');
            return;
        }
        let emitFloor;
        this.floors.forEach(floor => {
            let visible = 'none';
            if (opts.newFloor.id === floor.floor.id) {
                visible = 'visible';
                emitFloor = floor.floor;
            }
            floor.layers.forEach(layerName => {
                if (!window.mapInMemory) {
                    return;
                }
                const verifyLayer = window.mapInMemory.getLayer(layerName);
                if (verifyLayer) {
                    window.mapInMemory.setLayoutProperty(layerName, 'visibility', visible);
                    this.log('debug', 'changeFloor: layer visibility set layerName: ', layerName, ' visibility: ', visible);
                }
            });
        });
        this.removeAllPins();
        let floorChanged = true;
        if (emitFloor !== undefined) {
            if (this.currentFloorId === emitFloor.id) {
                floorChanged = false;
            }
            this.currentFloorId = emitFloor.id;
            window.currentFloorId = emitFloor.id;
            this.log('debug', 'changeFloor: setting current floor id: ', emitFloor.id);
        }
        if (emitFloor !== undefined && opts.displayNotification && floorChanged) {
            this.log('debug', 'emit floorchange floor: ', emitFloor);
            this.emit('floorchange', emitFloor);
        }
        this.displayYouAreHere();
        if (opts.displayDefaultAmenities) {
            this.displayDefaultAmenities();
        }
    }
    displaySingleRoute(routeLineIndex) {
        const routeLine = this.routingLines[routeLineIndex];
        const p0 = window.mapInMemory.project([routeLine.bbox[0] - 0.0003, routeLine.bbox[1] - 0.0003]);
        const p1 = window.mapInMemory.project([routeLine.bbox[2] + 0.0003, routeLine.bbox[3] + 0.0003]);
        if (routeLine.firstRender) {
            this.routingLines[routeLineIndex].firstRender = false;
            this.log('debug', p0, p1, routeLine.bearing, routeLine.options);
            if (routeLine.path.Coordinates.length === 1) {
                routeLine.path.Coordinates.push(routeLine.path.Coordinates[0]);
            }
            this.addAnimationToRoute(routeLine.path, routeLine.floor, routeLine.lineName, routeLine.lineGeo);
        }
        window.mapInMemory.fitScreenCoordinates(p0, p1, routeLine.bearing, routeLine.options);
        setTimeout(() => {
            if (this.routingLines.length <= 1) {
                return;
            }
            const style = "border-radius: 16px; background-color: white; padding: 10px; color: #555555; font-weight: bold; font-size: 12px; line-height: 12px; display: grid; grid-template-columns: auto auto;";
            let nextStep, prevStep;
            if (routeLineIndex < this.routingLines.length - 1) {
                nextStep = document.createElement("div");
                nextStep.className = 'routing-next-step';
                nextStep.innerHTML = `<div style="${style}"><div>To ${this.routingLines[routeLineIndex + 1].floor.floor.name}</div><svg xmlns="http://www.w3.org/2000/svg" height="12px" viewBox="0 -960 960 960" width="12px" fill="#555555"><path d="m321-80-71-71 329-329-329-329 71-71 400 400L321-80Z"/></svg></div>`;
                nextStep.onclick = () => {
                    this.changeFloor({
                        newFloor: this.routingLines[routeLineIndex + 1].floor.floor,
                        displayNotification: true,
                    });
                };
            }
            if (routeLineIndex > 0) {
                prevStep = document.createElement("div");
                prevStep.className = 'routing-next-step';
                prevStep.innerHTML = `<div style="${style}"><svg xmlns="http://www.w3.org/2000/svg" height="12px" viewBox="0 -960 960 960" width="12px" fill="#555555"><path d="M400-80 0-480l400-400 71 71-329 329 329 329-71 71Z"/></svg><div>From ${this.routingLines[routeLineIndex - 1].floor.floor.name}</div></div>`;
                prevStep.onclick = () => {
                    this.changeFloor({
                        newFloor: this.routingLines[routeLineIndex - 1].floor.floor,
                        displayNotification: true,
                    });
                };
            }
            const firstCoord = routeLine.path.Coordinates[0];
            const lastCoord = routeLine.path.Coordinates[routeLine.path.Coordinates.length - 1];
            const dist = turf.distance(firstCoord, lastCoord);
            if (prevStep && nextStep && dist < 0.01) {
                const parentEl = document.createElement("div");
                parentEl.appendChild(nextStep);
                parentEl.appendChild(prevStep);
                this.setMarker({
                    coord: { lng: lastCoord[0].toString(), lat: lastCoord[1].toString() },
                    html: '',
                    htmlElement: parentEl,
                });
            }
            else {
                if (nextStep) {
                    this.setMarker({
                        coord: { lng: lastCoord[0].toString(), lat: lastCoord[1].toString() },
                        html: '',
                        htmlElement: nextStep,
                    });
                }
                if (prevStep) {
                    this.setMarker({
                        coord: { lng: firstCoord[0].toString(), lat: firstCoord[1].toString() },
                        html: '',
                        htmlElement: prevStep,
                    });
                }
            }
        }, 100);
    }
    createMapDiv(elementId) {
        this.log('debug', 'createMapDiv: called, elementId: ', elementId);
        let newDiv = document.getElementById(this.containerId);
        if (!newDiv) {
            this.log('debug', 'createMapDiv: div does not exist creating');
            newDiv = document.createElement('div');
            newDiv.setAttribute('id', this.containerId);
        }
        this.log('debug', 'createMapDiv: appending div to element: ', elementId);
        const elem = document.getElementById(elementId);
        if (elem) {
            elem.appendChild(newDiv);
        }
        else {
            this.log('error', 'Element not found');
        }
        return this.containerId;
    }
    findBuildingByLocationId(locationId, includeCenter = false) {
        let floorIndex;
        let foundBuilding;
        const hits = [];
        for (const floor of this.floors) {
            const building = floor.clickable.find(feature => feature.properties.skyOSLocationId === locationId);
            if (building) {
                hits.push({
                    floor,
                    building,
                });
            }
        }
        if (hits.length <= 0) {
            return null;
        }
        const currentFloorHit = hits.find((hit) => hit.floor.floor.id === this.currentFloorId);
        if (currentFloorHit) {
            floorIndex = currentFloorHit.floor.index;
            foundBuilding = currentFloorHit.building;
        }
        else {
            floorIndex = hits[0].floor.index;
            foundBuilding = hits[0].building;
        }
        if (floorIndex === undefined || foundBuilding === undefined) {
            return null;
        }
        const returnData = {
            feature: foundBuilding,
            floorIndex
        };
        if (includeCenter) {
            const centerPoint = turf.center(foundBuilding);
            returnData.center = centerPoint.geometry.coordinates;
        }
        return returnData;
    }
    highlightLocation(opts) {
        this.log('debug', 'highlightLocation: called, opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        const isValidColorHex = /^#[0-9A-F]{6}$/i.test(opts.color);
        if (!isValidColorHex) {
            this.log('error', 'invalid hex color passed to highlightLocation:', opts.color);
            return;
        }
        const found = this.findBuildingByLocationId(opts.location.id);
        if (!found || !found.feature) {
            this.log('error', 'highlightLocation: called, location id not found: ', opts.location.id);
            return;
        }
        this.removeHighlights();
        if (opts.changeFloor) {
            const floor = this.getFloorByIndex(found.floorIndex);
            if (floor) {
                this.changeFloor({ newFloor: { id: floor.floor.id } });
            }
        }
        const buildingHighlightHeight = _.get(this.settings, 'venue.settings.mapboxSettings.buildingHighlightHeight', 11);
        const clickedHeight = parseFloat(buildingHighlightHeight.toString());
        window.mapInMemory.setPaintProperty(`floor-${found.floorIndex}`, 'fill-extrusion-color', [
            'case',
            ['==', ['get', 'skyOSLocationId'], opts.location.id],
            opts.color,
            ['get', 'fillExtrusionColor']
        ]);
        const labels3D = _.get(this.settings, 'venue.settings.mapboxSettings.threeJsLabels', false);
        if (!labels3D) {
            window.mapInMemory.setPaintProperty(`floor-${found.floorIndex}`, 'fill-extrusion-height', [
                'case',
                ['==', ['get', 'skyOSLocationId'], opts.location.id],
                clickedHeight,
                ['get', 'fillExtrusionHeight']
            ]);
        }
    }
    getStyleMap() {
        const screenMapboxStyle = _.get(this.settings, 'screen.settings.mapboxStyle');
        if (screenMapboxStyle) {
            return screenMapboxStyle;
        }
        const venueMapboxStyle = _.get(this.settings, 'venue.settings.mapboxSettings.backgroundMap');
        if (venueMapboxStyle) {
            return venueMapboxStyle;
        }
        return 'mapbox://styles/smartlinkdev/cjsvxbi218mgz1gkbye4y2pz0';
    }
    getKioskLocation(useDefault = false) {
        if (!this.settings.kiosk && this.settings.venue.settings.web && this.settings.venue.settings.web.defaultMapCenter) {
            const lng = this.settings.venue.settings.web.defaultMapCenter.lng.toString();
            const lat = this.settings.venue.settings.web.defaultMapCenter.lat.toString();
            return [parseFloat(lng), parseFloat(lat)];
        }
        if (!this.settings.kiosk) {
            return [0, 0];
        }
        const defaultCenterLat = _.get(this.settings.kiosk, 'settings.defaultCenterLatitude');
        const defaultCenterLng = _.get(this.settings.kiosk, 'settings.defaultCenterLongitude');
        if (useDefault && defaultCenterLat && defaultCenterLng) {
            return [defaultCenterLng, defaultCenterLat];
        }
        return [parseFloat(this.settings.kiosk.longitude.toString()), parseFloat(this.settings.kiosk.latitude.toString())];
    }
    insideGeofence() {
        var _a, _b, _c, _d, _e, _f;
        const [mapCenterLongitude, mapCenterLatitude] = this.getKioskLocation(true);
        let geofenceNW = _get(this.settings, 'venue.settings.web.geofenceNW');
        let geofenceSE = _get(this.settings, 'venue.settings.web.geofenceSE');
        if (!geofenceNW ||
            !geofenceSE ||
            !geofenceNW.latitude ||
            !geofenceNW.longitude ||
            !geofenceSE.latitude ||
            !geofenceSE.longitude) {
            if (mapCenterLongitude === 0 && mapCenterLatitude === 0) {
                this.log('error', 'Map center is [0, 0], geofence check cannot be performed');
                return true;
            }
            const distance = 1;
            const boundingBox = turf.bbox(turf.circle([mapCenterLongitude, mapCenterLatitude], distance));
            geofenceNW = {
                latitude: boundingBox[3],
                longitude: boundingBox[0],
            };
            geofenceSE = {
                latitude: boundingBox[1],
                longitude: boundingBox[2],
            };
            this.log('debug', 'geofence generated', geofenceNW, geofenceSE);
        }
        const currentLat = ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.latitude) || 0;
        const currentLong = ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.longitude) || 0;
        if (currentLong < geofenceNW.longitude || currentLong > geofenceSE.longitude) {
            this.log('debug', 'outside geofence long', currentLong, geofenceNW.longitude, geofenceSE.longitude);
            return false;
        }
        if (currentLat > geofenceNW.latitude || currentLat < geofenceSE.latitude) {
            this.log('debug', 'outside geofence lat', currentLat, geofenceNW.latitude, geofenceSE.latitude);
            return false;
        }
        this.log('debug', 'geofence setup and inside geofence', this.settings.currentPosition, geofenceNW, geofenceSE);
        return true;
    }
    getMapCenter(useKioskDefault = false) {
        this.log('debug', 'mapCenter', this.settings.currentPosition);
        if (this.settings.currentPosition && this.settings.currentPosition.coords) {
            const insideGeofence = this.insideGeofence();
            if (insideGeofence) {
                return new mapboxgl.LngLat(this.settings.currentPosition.coords.longitude, this.settings.currentPosition.coords.latitude);
            }
            this.log('debug', 'Currently outside geofence');
        }
        const coords = this.getKioskLocation(useKioskDefault);
        this.log('debug', 'center cords', coords);
        return new mapboxgl.LngLat(coords[0], coords[1]);
    }
    getDefaultZoom() {
        if (this.settings.kiosk && this.settings.kiosk.settings.defaultZoom) {
            return this.settings.kiosk.settings.defaultZoom;
        }
        let zoom = _.get(this.settings, 'venue.settings.mapboxSettings.defaultZoom', 18.2);
        if (this.browserSettings.isMobile) {
            zoom = _.get(this.settings, 'venue.settings.mapboxSettings.mobileZoom', zoom);
        }
        return Number(zoom);
    }
    getDefaultPitch() {
        if (this.settings.kiosk && this.settings.kiosk.settings.defaultPitch) {
            return this.settings.kiosk.settings.defaultPitch;
        }
        let pitch = _.get(this.settings, 'venue.settings.mapboxSettings.defaultPitch', 20);
        if (this.browserSettings.isMobile) {
            pitch = _.get(this.settings, 'venue.settings.mapboxSettings.mobilePitch', pitch);
        }
        return Number(pitch);
    }
    getDefaultBearing(ignoreFor3dLabels) {
        if (!ignoreFor3dLabels && _.get(this.settings, 'venue.settings.mapboxSettings.threeJsLabels')) {
            return 0;
        }
        if (this.browserSettings.isMobile) {
            return _.get(this.settings, 'venue.settings.web.defaultMobileBearing', 0);
        }
        let defaultBearing = _.get(this.settings, 'screen.settings.defaultBearing');
        if (defaultBearing) {
            return defaultBearing;
        }
        defaultBearing = _.get(this.settings, 'venue.settings.mapboxSettings.defaultBearing');
        if (defaultBearing) {
            if (typeof defaultBearing === 'string')
                return parseInt(defaultBearing, 10);
            return defaultBearing;
        }
        return 0;
    }
    getMapBounds() {
        var _a;
        let west, east, north, south;
        if (!((_a = this.settings) === null || _a === void 0 ? void 0 : _a.kiosk)) {
            west = parseFloat(_get(this.settings, 'venue.settings.web.mapBounds.west', '0.01').toString());
            east = parseFloat(_get(this.settings, 'venue.settings.web.mapBounds.east', '0.01').toString());
            north = parseFloat(_get(this.settings, 'venue.settings.web.mapBounds.north', '0.01').toString());
            south = parseFloat(_get(this.settings, 'venue.settings.web.mapBounds.south', '0.01').toString());
        }
        else {
            west = parseFloat(_get(this.settings, 'kiosk.settings.westernBoundOffset', '0.01').toString());
            east = parseFloat(_get(this.settings, 'kiosk.settings.easternBoundOffset', '0.01').toString());
            north = parseFloat(_get(this.settings, 'kiosk.settings.northernBoundOffset', '0.01').toString());
            south = parseFloat(_get(this.settings, 'kiosk.settings.southernBoundOffset', '0.01').toString());
        }
        const center = this.getMapCenter();
        const coords = [
            center.lng - west,
            center.lat - south,
            center.lng + east,
            center.lat + north
        ];
        return coords;
    }
    generateMapboxConfig() {
        var _a;
        let zoom = this.getDefaultZoom();
        if (!((_a = this === null || this === void 0 ? void 0 : this.browserSettings) === null || _a === void 0 ? void 0 : _a.disableInitialFlyin)) {
            zoom = zoom - 3;
        }
        const container = document.getElementById(this.containerId);
        if (!container) {
            throw Error('container not found');
        }
        const startBearing = this.getDefaultBearing(false);
        return {
            container,
            style: this.getStyleMap(),
            center: this.getMapCenter(true),
            zoom,
            pitch: this.getDefaultPitch(),
            bearing: startBearing,
            antialias: true,
            maxBounds: this.getMapBounds(),
            logoPosition: this.logoPosition,
            minZoom: this.getMinZoom(),
            maxZoom: this.getMaxZoom()
        };
    }
    getMinZoom() {
        const zoom = _get(this.settings, 'venue.settings.mapboxSettings.minZoom');
        if (zoom) {
            return zoom;
        }
        return 4;
    }
    getMaxZoom() {
        const zoom = _get(this.settings, 'venue.settings.mapboxSettings.maxZoom');
        if (zoom) {
            return zoom;
        }
        return 22;
    }
    init() {
        this.log('debug', 'init: called');
        window.mapShown = true;
        if ('mapInMemory' in window) {
            this.log('debug', 'init: map already exists');
            this.emit('ready');
        }
        else {
            this.log('debug', 'init: generating new map');
            mapboxgl.accessToken = accessKey;
            if (window.navigator && window.navigator.hardwareConcurrency) {
                mapboxgl.workerCount = window.navigator.hardwareConcurrency - 1;
            }
            try {
                window.mapInMemory = new mapboxgl.Map(this.generateMapboxConfig());
                this.log('debug', 'map created', window.mapInMemory);
                window.mapInMemory.on('load', () => {
                    this.log('debug', 'init: map loaded');
                    this.loadContent();
                    this.sendLoadReport();
                });
                window.mapInMemory.on('error', (err) => {
                    this.log('error', `mapbox error: ${err.message}`, err);
                    Sentry.captureException(err);
                    this.emit('error', {
                        message: err.message,
                        name: 'error'
                    });
                });
            }
            catch (e) {
                const err = e;
                this.log('error', `mapbox error: ${err.message}`, err);
                Sentry.captureException(err);
                this.emit('error', {
                    message: err.message,
                    name: 'error'
                });
            }
        }
    }
    addResetButton() {
        const resetButton = document.createElement('button');
        resetButton.setAttribute('class', 'mapboxgl-ctrl-reset');
        resetButton.setAttribute('type', 'button');
        resetButton.setAttribute('title', 'Reset');
        resetButton.setAttribute('aria-label', 'Reset map');
        const config = {
            notify: () => {
                return;
            }
        };
        resetButton.onclick = () => this.reset(config);
        const resetIcon = document.createElement('span');
        resetIcon.setAttribute('class', 'mapboxgl-ctrl-icon');
        resetIcon.setAttribute('aria-hidden', 'true');
        resetIcon.style.backgroundImage =
            "url(\"data:image/svg+xml;charset=utf-8,%3Csvg width='19' height='19' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' fill='%23fff'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3Cpath d='M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z' fill='%23222'/%3E%3C/svg%3E\")";
        resetButton.appendChild(resetIcon);
        const ctrlGroup = document.querySelector('.mapboxgl-ctrl-group:not(#floor-changer)');
        if (ctrlGroup) {
            ctrlGroup.prepend(resetButton);
        }
        return Promise.resolve();
    }
    addNavigationControls(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!window.mapInMemory) {
                return Promise.resolve();
            }
            window.mapInMemory.addControl(new mapboxgl.NavigationControl(config.options), config.position || 'bottom-left');
            if (config.showReset) {
                yield this.addResetButton();
            }
            return Promise.resolve();
        });
    }
    styleMap() {
        if (!window.mapInMemory) {
            return;
        }
        const backgroundLayer = window.mapInMemory.getLayer('background');
        if (backgroundLayer) {
            let mapBackground = _.get(this.settings, 'venue.settings.mapboxSettings.mapBackgroundColor', '#333333');
            if (mapBackground !== '') {
                try {
                    window.mapInMemory.setPaintProperty('background', 'fill-color', mapBackground);
                }
                catch (e) {
                    window.mapInMemory.setPaintProperty('background', 'background-color', mapBackground);
                }
            }
        }
        const roadLayer = window.mapInMemory.getLayer('road');
        const roadColor = _.get(this.settings, 'venue.settings.mapboxSettings.streetAndPathColor', '#ffffff');
        if (roadLayer) {
            window.mapInMemory.setPaintProperty('road', 'line-color', roadColor);
        }
        const pedPath = window.mapInMemory.getLayer('ped path');
        if (pedPath) {
            window.mapInMemory.setPaintProperty('ped path', 'line-color', roadColor);
        }
    }
    loadContent() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (!window.mapInMemory) {
                return Promise.resolve();
            }
            yield this.addFloors();
            if (!this.debugDisable3dObjects) {
                this.add3dObjects();
            }
            if (this.screenId > 0 || this.settings.useRoutingLibrary) {
                yield this.loadRoutes();
            }
            this.changeFloor({ newFloor: { id: this.getDefaultFloorId() } });
            this.log('debug', 'this.currentFloorId', this.currentFloorId);
            this.displayDefaultAmenities();
            window.mapInMemory.setBearing(this.getDefaultBearing(true));
            this.styleMap();
            this.emit('readyToDisplay');
            if (!((_a = this === null || this === void 0 ? void 0 : this.browserSettings) === null || _a === void 0 ? void 0 : _a.disableInitialFlyin)) {
                this.animate({
                    duration: 0.4,
                    zoom: this.getDefaultZoom(),
                    moveTo: {
                        lat: this.getMapCenter(true).lat.toString(),
                        lng: this.getMapCenter(true).lng.toString()
                    },
                    rotation: this.getDefaultBearing(true),
                    pitch: this.getDefaultPitch()
                });
            }
            this.log('debug', 'loadContent complete emit ready');
            this.emit('ready');
        });
    }
    loadRoutes() {
        return __awaiter(this, void 0, void 0, function* () {
            this.log('debug', 'Setting up map routing');
            if (this.settings.useRoutingLibrary) {
                this.mapboxRouter = yield MapboxRouter.Create(_get(this.settings, 'venue.settings.mapboxSettings'));
                this.log('debug', 'created map router');
            }
            else {
                this.routes = yield SkyOSMap.getPrecalculatedRoutes(this.screenId);
                this.log('debug', 'loaded precalculatedRoutes');
            }
            return Promise.resolve();
        });
    }
    getDefaultFloorId() {
        const defaultFloorWeb = _get(this.settings, 'venue.settings.mapboxSettings.web.defaultFloorId');
        if (defaultFloorWeb && !this.settings.kiosk) {
            return defaultFloorWeb;
        }
        const defaultFloor = _.get(this.settings, 'kiosk.settings.mapboxFloor');
        if (!defaultFloor && this.floors.length > 0) {
            return this.floors[0].floor.id;
        }
        if (!defaultFloor) {
            throw new Error('ERROR: No default floor set for this kiosk');
        }
        return defaultFloor;
    }
    addFloors() {
        return __awaiter(this, void 0, void 0, function* () {
            const floorsRaw = _get(this.settings, 'venue.settings.mapboxSettings.floors', []);
            const floors = floorsRaw.map((floor) => {
                if (floor.dataset_id) {
                    floor.id = `roveiq-${floor.dataset_id}`;
                }
                return floor;
            });
            const promises = floors.map((floor) => {
                return this.addFloor(floor, floor.index);
            });
            yield Promise.all(promises);
        });
    }
    addFloor(floor, index) {
        return __awaiter(this, void 0, void 0, function* () {
            this.log('debug', 'Getting floor dataset: ', floor.id);
            const dataset = yield SkyOSMap.getMapboxDataset(floor);
            const cleanDataset = this.cleanDataset(dataset);
            const mapboxFloor = this.generateMapboxFloor(cleanDataset, index, floor);
            this.log('debug', 'addFloor: ', mapboxFloor);
            yield this.addFloorToMap(mapboxFloor);
            this.floors.push(mapboxFloor);
        });
    }
    cleanDataset(dataset) {
        dataset.features = dataset.features.map((feature) => {
            const props = Object.keys(feature.properties);
            props.forEach((prop) => {
                if (feature.properties[prop] === null || feature.properties[prop] === undefined || feature.properties[prop] === '') {
                    delete feature.properties[prop];
                }
            });
            return feature;
        });
        return dataset;
    }
    FeatureClick(event) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.log('debug', 'Feature was clicked');
            if (!window.mapInMemory) {
                return Promise.resolve();
            }
            const features = window.mapInMemory.queryRenderedFeatures(event.point);
            if (features.length > 0) {
                let useFeature;
                features.forEach((feature) => {
                    if (!useFeature && feature.properties && feature.properties.skyOSLocationId > 0) {
                        useFeature = feature;
                    }
                });
                this.log('debug', 'Feature clicked: ', useFeature);
                const skyosLocationId = ((_a = useFeature.properties) === null || _a === void 0 ? void 0 : _a.skyOSLocationId) || -1;
                if (skyosLocationId === -1) {
                    return Promise.resolve();
                }
                const location = this.settings.locations.find(loc => loc.id === skyosLocationId);
                if (location) {
                    const disabled = (location.disabled
                        && typeof location.disabled === 'string'
                        && location.disabled.length > 0);
                    if (disabled) {
                        return Promise.resolve();
                    }
                    this.emit('locationClick', {
                        location,
                        latLng: event.lngLat
                    });
                }
                if (!this.browserSettings.disableClickRouting) {
                    this.routeToLocation({
                        location: {
                            id: skyosLocationId
                        }
                    });
                }
            }
        });
    }
    addFloorToMap(floor) {
        if (!window.mapInMemory) {
            return;
        }
        const backgroundLayers = this.backgroundLayerSettings(floor);
        backgroundLayers.forEach(layer => {
            if (window.mapInMemory) {
                window.mapInMemory.addLayer(layer);
                floor.layers.push(layer.id);
            }
        });
        const layers = this.layerSettings(floor);
        layers.forEach(layer => {
            if (window.mapInMemory) {
                window.mapInMemory.addLayer(layer);
                floor.layers.push(layer.id);
            }
        });
        window.mapInMemory.on('click', `floor-${floor.index}`, e => {
            this.FeatureClick(e);
        });
        window.mapInMemory.on('click', `floor-clickableBackground-${floor.index}`, e => {
            this.FeatureClick(e);
        });
        this.addLabelLayer(floor);
        if (!this.debugDisable3dNames) {
            this.add3DLabelLayer(floor);
        }
    }
    addFloorChanger(config) {
        const floors = _get(this.settings, 'venue.settings.mapboxSettings.floors', []);
        if (floor.length > 1) {
            const floorChanger = document.createElement('div');
            floorChanger.setAttribute('id', 'floor-changer');
            floorChanger.setAttribute('class', 'mapboxgl-ctrl mapboxgl-ctrl-group');
            floors.forEach(floor => {
                const button = document.createElement('button');
                button.onclick = () => this.changeFloor({ newFloor: floor });
                button.setAttribute('id', `floor-${floor.id}`);
                button.setAttribute('class', 'floor-ctrl');
                button.setAttribute('type', 'button');
                button.setAttribute('title', floor.name);
                button.setAttribute('aria-label', floor.name);
                button.style.width = '100%';
                button.style.padding = '2px 6px';
                button.innerText = floor.name;
                floorChanger.appendChild(button);
            });
            const ctrlGroup = document.getElementsByClassName(`mapboxgl-ctrl-${config.position || 'bottom-left'}`);
            if (ctrlGroup) {
                ctrlGroup[0].appendChild(floorChanger);
            }
        }
        return Promise.resolve();
    }
    add3DLabelLayer(floor) {
        if (!window.mapInMemory) {
            return;
        }
        const labels3D = _.get(this.settings, 'venue.settings.mapboxSettings.threeJsLabels', false);
        if (!labels3D) {
            return;
        }
        const titleColor = _.get(this.settings, 'venue.settings.mapboxSettings.titleColor', '#111111');
        const loader = new FontLoader();
        let fontLoaded;
        if (this.settings.venue.id !== 9 && this.settings.venue.id !== 90) {
            fontLoaded = loader.parse(defaultFont);
        }
        else {
            fontLoaded = loader.parse(mddFont);
        }
        const scale = projectedUnitsPerMeter(this.getMapCenter().lat);
        const color = new THREE.Color(titleColor);
        const material = new THREE.MeshBasicMaterial({ color });
        const buildingHighlightHeight = _.get(this.settings, 'venue.settings.mapboxSettings.buildingHeight', 10);
        const buildingHeight = parseFloat(buildingHighlightHeight.toString());
        const layer = new ThreeJSTextLayer({
            font: fontLoaded,
            scale,
            material,
            floor,
            buildingHeight,
            mapCenter: this.getMapCenter().toArray()
        });
        this.log('debug', 'Adding 3d text layer');
        window.mapInMemory.addLayer(layer);
        floor.layers.push(layer.id);
    }
    addLabelLayer(floor) {
        if (!floor.labels || floor.labels.length == 0 || !window.mapInMemory) {
            return;
        }
        const settings = this.labelLayerSettings(floor);
        this.log('debug', 'Adding labels layer');
        window.mapInMemory.addLayer(settings);
        floor.layers.push(settings.id);
    }
    labelLayerSettings(floor) {
        const labelFontSize = _.get(this.settings, 'venue.settings.mapboxSettings.labelFontSize', 16);
        const fontSize = parseInt(labelFontSize.toString(), 10);
        return {
            id: `floor-${floor.index}-labels`,
            type: 'symbol',
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: floor.labels
                },
                tolerance: 0.00001
            },
            layout: {
                visibility: 'none',
                'text-field': '{name}',
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-offset': [0, 0],
                'text-anchor': 'top',
                'text-pitch-alignment': 'map',
                'text-size': fontSize
            },
            paint: {
                'text-color': '#ffffff',
                'text-halo-color': '#000000',
                'text-halo-width': 2,
                'text-halo-blur': 1,
                'text-translate-anchor': 'map'
            }
        };
    }
    backgroundLayerSettings(floor) {
        const noClickBack = floor.notClickable.filter(feature => feature.properties.background);
        const styleNames = [];
        noClickBack.forEach(feature => {
            styleNames.push(feature.properties.class || 'noclick');
        });
        const cleanClassNames = _.uniq(styleNames);
        return cleanClassNames.map((className, index) => {
            const classFeatures = noClickBack.filter(feature => feature.properties.class === className);
            return {
                id: `floor-${floor.index}-noclick-background-${index}`,
                type: 'fill-extrusion',
                source: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: classFeatures
                    },
                    tolerance: 0.00001
                },
                layout: { visibility: 'none' },
                paint: {
                    'fill-extrusion-height': ['get', 'fillExtrusionHeight'],
                    'fill-extrusion-base': ['get', 'fillExtrusionBaseHeight'],
                    'fill-extrusion-color': ['get', 'fillExtrusionColor'],
                    'fill-extrusion-opacity': classFeatures[0].properties.fillExtrusionOpacity
                }
            };
        });
    }
    layerSettings(floor) {
        const layers = [];
        const buildingOpacity = _.get(this.settings, 'venue.settings.mapboxSettings.buildingOpacity', 1);
        const fillExtrusionOpacity = parseFloat(buildingOpacity.toString()) / 100;
        layers.push({
            id: `floor-clickableBackground-${floor.index}`,
            type: 'fill-extrusion',
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: floor.clickableBackground,
                },
                tolerance: 0.00001
            },
            layout: { visibility: 'none' },
            paint: {
                'fill-extrusion-height': ['get', 'fillExtrusionHeight'],
                'fill-extrusion-base': ['get', 'fillExtrusionBaseHeight'],
                'fill-extrusion-color': ['get', 'fillExtrusionColor'],
                'fill-extrusion-opacity': fillExtrusionOpacity,
            }
        });
        layers.push({
            id: `floor-${floor.index}`,
            type: 'fill-extrusion',
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: floor.clickable
                },
                tolerance: 0.00001
            },
            layout: { visibility: 'none' },
            paint: {
                'fill-extrusion-height': ['get', 'fillExtrusionHeight'],
                'fill-extrusion-base': ['get', 'fillExtrusionBaseHeight'],
                'fill-extrusion-color': ['get', 'fillExtrusionColor'],
                'fill-extrusion-opacity': fillExtrusionOpacity,
            }
        });
        const noClickFront = floor.notClickable.filter(feature => !feature.properties.background);
        const styleNames = [];
        noClickFront.forEach(feature => {
            styleNames.push(feature.properties.class || 'noclick');
        });
        const cleanClassNames = _.uniq(styleNames);
        cleanClassNames.forEach((className, index) => {
            const classFeatures = noClickFront.filter(feature => feature.properties.class === className);
            layers.push({
                id: `floor-${floor.index}-noclick-${index}`,
                type: 'fill-extrusion',
                source: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: classFeatures
                    },
                    tolerance: 0.00001
                },
                layout: { visibility: 'none' },
                paint: {
                    'fill-extrusion-height': ['get', 'fillExtrusionHeight'],
                    'fill-extrusion-base': ['get', 'fillExtrusionBaseHeight'],
                    'fill-extrusion-color': ['get', 'fillExtrusionColor'],
                    'fill-extrusion-opacity': classFeatures[0].properties.fillExtrusionOpacity
                }
            });
        });
        if (floor.stairs.length > 0) {
            const stairsOpacity = _.get(this.settings, 'venue.settings.mapboxSettings.stairsOpacity', 1);
            const stairLayer = {
                id: `floor-${floor.index}-stairs`,
                type: 'fill-extrusion',
                source: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: floor.stairs
                    },
                    tolerance: 0.00001
                },
                layout: { visibility: 'none' },
                paint: {
                    'fill-extrusion-height': ['get', 'fillExtrusionHeight'],
                    'fill-extrusion-base': ['get', 'fillExtrusionBaseHeight'],
                    'fill-extrusion-color': ['get', 'fillExtrusionColor'],
                    'fill-extrusion-opacity': parseFloat(stairsOpacity.toString()) / 100
                }
            };
            layers.push(stairLayer);
        }
        return layers;
    }
    generateMapboxFloor(dataset, index, floor) {
        const clickable = this.getDatasetClickable(dataset, floor, false);
        const clickableBackground = this.getDatasetClickable(dataset, floor, true);
        return {
            floor,
            index,
            navLines: this.getDatasetNavLines(dataset),
            clickable,
            clickableBackground,
            notClickable: this.getDatasetNotClickable(dataset, floor),
            stairs: this.getDatasetStairs(dataset, floor),
            labels: this.getDatasetLabels(clickable),
            layers: []
        };
    }
    getDatasetLabels(clickable) {
        const hideLabels = _.get(this.settings, 'venue.settings.mapboxSettings.disableLabels', false);
        if (hideLabels) {
            return [];
        }
        const labels = _.cloneDeep(clickable);
        return labels.map(f => {
            var _a;
            f.geometry.type = 'Point';
            f.geometry.coordinates = ((_a = f.properties) === null || _a === void 0 ? void 0 : _a.titleLocation) || [0, 0];
            return f;
        });
    }
    getDatasetStairs(dataset, floor) {
        const height = _.get(this.settings, 'venue.settings.mapboxSettings.stairsHeight', 1);
        const color = _.get(this.settings, 'venue.settings.mapboxSettings.stairsColor', '#ffffff');
        const stairsOpacity = _.get(this.settings, 'venue.settings.mapboxSettings.stairsOpacity', 0);
        const opacity = parseFloat(stairsOpacity.toString()) / 100;
        const base = 0;
        return dataset.features
            .filter(f => f.geometry.type == 'Polygon')
            .filter(f => 'floors' in f.properties)
            .map(feature => {
            feature.properties.floorId = floor.id;
            feature.properties.type = 'noclick';
            feature.properties.clickable = false;
            feature.properties.fillExtrusionHeight = Number(height);
            feature.properties.fillExtrusionBaseHeight = Number(base);
            feature.properties.fillExtrusionColor = color;
            feature.properties.fillExtrusionOpacity = Number(opacity);
            return feature;
        });
    }
    getDatasetNotClickable(dataset, floor) {
        return dataset.features
            .filter(f => f.geometry.type == 'Polygon')
            .filter(f => !('floors' in f.properties))
            .filter(f => 'class' in f.properties && f.properties.class && f.properties.class.includes('noclick'))
            .map(f => this.prepareMapboxNoClick(f, floor));
    }
    prepareMapboxNoClick(feature, floor) {
        feature.properties.floorId = floor.id;
        feature.properties.type = 'noclick';
        feature.properties.clickable = false;
        const clickNum = (feature.properties.class || 'noclick').substring(7);
        const defaultNoClicks = ['', '2', '3', '4'];
        let heightPath = `venue.settings.mapboxSettings.nonclickable${clickNum}Height`;
        let basePath = `venue.settings.mapboxSettings.nonclickable${clickNum}BaseHeight`;
        let colorPath = `venue.settings.mapboxSettings.nonclickable${clickNum}Color`;
        let opacityPath = `venue.settings.mapboxSettings.nonclickable${clickNum}Opacity`;
        let background = false;
        const customNoClickLayers = _.get(this.settings, 'venue.settings.mapboxSettings.customStyle', []);
        if (!defaultNoClicks.includes(clickNum)) {
            const index = customNoClickLayers.findIndex(style => style.class === feature.properties.class);
            if (index >= 0) {
                const clickLayer = customNoClickLayers[index];
                heightPath = `venue.settings.mapboxSettings.customStyle[${index}].height`;
                basePath = `venue.settings.mapboxSettings.customStyle[${index}].baseHeight`;
                colorPath = `venue.settings.mapboxSettings.customStyle[${index}].color`;
                opacityPath = `venue.settings.mapboxSettings.customStyle[${index}].opacity`;
                background = (clickLayer === null || clickLayer === void 0 ? void 0 : clickLayer.background) || false;
            }
        }
        feature.properties.fillExtrusionHeight = Number(_.get(this.settings, heightPath, 1));
        feature.properties.fillExtrusionBaseHeight = Number(_.get(this.settings, basePath, 0));
        feature.properties.fillExtrusionColor = _.get(this.settings, colorPath, '#ffffff');
        const opacity = _.get(this.settings, opacityPath, 0);
        feature.properties.fillExtrusionOpacity = parseFloat(opacity.toString()) / 100;
        feature.properties.background = background;
        return feature;
    }
    getDatasetClickable(dataset, floor, background) {
        return dataset.features
            .filter(f => f.geometry.type == 'Polygon')
            .filter(f => !('floors' in f.properties))
            .filter(f => !('class' in f.properties) || !f.properties.class || !f.properties.class.includes('noclick'))
            .map(f => this.prepareMapboxClickable(f, floor))
            .filter(f => (f.properties.background === background));
    }
    prepareMapboxClickable(feature, floor) {
        feature.properties.floorId = floor.id;
        feature.properties.type = 'clickable';
        feature.properties.clickable = false;
        feature.properties.skyOSLocationId = this.getFeatureLocationId(feature);
        feature.properties.background = false;
        feature.properties.name = this.getFeatureName(feature);
        feature.properties.door = this.getFeatureCoords(feature, 'door');
        const centerOfMass = turf.centerOfMass(feature);
        feature.properties.titleLocation = centerOfMass.geometry.coordinates;
        feature.properties.titleSize = 2;
        feature.properties.titleRotation = _.get(this.settings, 'venue.settings.mapboxSettings.titleRotation', 0);
        feature.properties.fillExtrusionColor = _.get(this.settings, 'venue.settings.mapboxSettings.buildingTopColor', '#cccccc');
        const buildingHeight = _.get(this.settings, 'venue.settings.mapboxSettings.buildingHeight', 10);
        feature.properties.fillExtrusionHeight = parseFloat(buildingHeight.toString());
        feature.properties.fillExtrusionBaseHeight = Number(_.get(this.settings, 'venue.settings.mapboxSettings.buildingBaseHeight', 0));
        const buildingOpacity = _.get(this.settings, 'venue.settings.mapboxSettings.buildingOpacity', 0);
        feature.properties.fillExtrusionOpacity = parseFloat(buildingOpacity.toString()) / 100;
        if (feature.properties.skyOSLocationId > 0) {
            feature = this.featureOverrideProperties(feature);
        }
        return feature;
    }
    getFeatureName(feature) {
        if (feature.properties.name) {
            return feature.properties.name;
        }
        if (feature.properties.title) {
            return feature.properties.title;
        }
        return '';
    }
    featureOverrideProperties(feature) {
        const skyOSLocation = this.settings.locations.find(l => l.id == feature.properties.skyOSLocationId);
        if (!skyOSLocation) {
            return feature;
        }
        if (skyOSLocation.disabled) {
            const disabled = (skyOSLocation.disabled
                && typeof skyOSLocation.disabled === 'string'
                && skyOSLocation.disabled.length > 0);
            if (disabled) {
                const buildingHeight = _.get(this.settings, 'venue.settings.mapboxSettings.buildingHeight', 10);
                const normalHeight = parseFloat(buildingHeight.toString());
                const normalColor = _.get(this.settings, 'venue.settings.mapboxSettings.buildingTopColor', '#cccccc');
                feature.properties.fillExtrusionColor = normalColor;
                feature.properties.fillExtrusionHeight = Number(normalHeight);
                return feature;
            }
        }
        feature.properties.name = skyOSLocation.name;
        if (skyOSLocation.settings) {
            if (skyOSLocation.settings.titleLatitude && skyOSLocation.settings.titleLongitude) {
                feature.properties.titleLocation = [
                    skyOSLocation.settings.titleLongitude,
                    skyOSLocation.settings.titleLatitude
                ];
            }
            if (skyOSLocation.settings.titleDisplay) {
                feature.properties.name = skyOSLocation.settings.titleDisplay;
            }
            if (skyOSLocation.settings.titleSize) {
                feature.properties.titleSize = skyOSLocation.settings.titleSize;
            }
            if (skyOSLocation.settings.titleRotation === 0 || skyOSLocation.settings.titleRotation) {
                feature.properties.titleRotation = skyOSLocation.settings.titleRotation;
            }
            if (skyOSLocation.settings.buildingColor) {
                feature.properties.fillExtrusionColor = skyOSLocation.settings.buildingColor;
            }
            if (skyOSLocation.settings.buildingHeight) {
                feature.properties.fillExtrusionHeight = Number(skyOSLocation.settings.buildingHeight);
            }
            if (skyOSLocation.settings.buildingImage) {
                feature.properties.image = skyOSLocation.settings.buildingImage;
            }
            if (skyOSLocation.settings.buildingImageHeight) {
                feature.properties.imageHeight = skyOSLocation.settings.buildingImageHeight;
            }
            if (skyOSLocation.settings.buildingImageWidth) {
                feature.properties.imageWidth = skyOSLocation.settings.buildingImageWidth;
            }
            if (skyOSLocation.settings.buildingImageScale) {
                feature.properties.imageScale = skyOSLocation.settings.buildingImageScale;
            }
            if (skyOSLocation.settings.background) {
                feature.properties.background = skyOSLocation.settings.background;
            }
        }
        return feature;
    }
    getFeatureCoords(feature, prop) {
        if (prop in feature.properties && typeof feature.properties[prop] == 'string' && feature.properties[prop] != '') {
            const cleaned = feature.properties[prop].replace(/\s/g, '');
            const valid = /^\[-?\d*\.\d+,-?\d*\.\d+]$/.test(cleaned);
            if (!valid) {
                this.log('debug', `Invalid ${prop} cords from mapbox feature: ${feature.id}`, feature.properties[prop]);
                return null;
            }
            try {
                return JSON.parse(feature.properties[prop]);
            }
            catch (e) {
                this.log('debug', `Cannot parse ${prop} cords from mapbox feature: ${feature.id}`, feature.properties[prop]);
                return null;
            }
        }
        if (prop in feature.properties &&
            Array.isArray(feature.properties[prop]) &&
            feature.properties[prop].length === 2) {
            return [
                parseFloat(feature.properties[`${prop}`][0].toString()),
                parseFloat(feature.properties[`${prop}`][1].toString())
            ];
        }
        if (`${prop}_x` in feature.properties &&
            `${prop}_y` in feature.properties &&
            feature.properties[`${prop}_x`] &&
            feature.properties[`${prop}_y`]) {
            return [
                parseFloat(feature.properties[`${prop}_x`].toString()),
                parseFloat(feature.properties[`${prop}_y`].toString())
            ];
        }
        return null;
    }
    getFeatureLocationId(feature) {
        let skyOSLocationId = 0;
        if (feature.properties.location_id) {
            skyOSLocationId = parseInt(feature.properties.location_id.toString(), 10);
        }
        else if (feature.properties.loc_id) {
            skyOSLocationId = parseInt(feature.properties.loc_id.toString(), 10);
        }
        else if (feature.properties.id_location) {
            skyOSLocationId = parseInt(feature.properties.id_location.toString(), 10);
        }
        return skyOSLocationId;
    }
    getDatasetNavLines(dataset) {
        return dataset.features.filter(f => f.geometry.type == 'LineString' && f.geometry.coordinates.length > 0);
    }
    static getMapboxDataset(floor) {
        return new Promise(resolve => {
            if (floor.dataset_id) {
                axios
                    .get(`https://api.roveiq.com/v1/datasets/${floor.dataset_id}`)
                    .then(response => {
                    resolve(response.data);
                });
            }
            else {
                fetchMapboxDataset(floor.id)
                    .then((dataset) => {
                    resolve(dataset);
                })
                    .catch((err) => {
                    console.log('err fetching mapbox dataset:', err);
                });
            }
        });
    }
    static getPrecalculatedRoutes(screenId) {
        return __awaiter(this, void 0, void 0, function* () {
            const res = yield axios.get('https://api.roveiq.com/v1/routes', {
                params: {
                    screenid: screenId
                }
            });
            return Promise.resolve(res.data.data);
        });
    }
    add3dObjects() {
        const objects = _get(this.settings, 'venue.settings.mapboxSettings.customObjects', []);
        if (objects.length == 0) {
            return;
        }
        objects.map((object, index) => {
            if (!window.mapInMemory) {
                return;
            }
            const firstLayer = this.floors[0] && this.floors[0].layers ? this.floors[0].layers[0] : undefined;
            const newLayer = new Model3D(object, index);
            const inBackground = _.get(object, 'background');
            this.log('debug', 'add3dObjects: ', object);
            if (inBackground) {
                window.mapInMemory.addLayer(newLayer, firstLayer);
            }
            else {
                window.mapInMemory.addLayer(newLayer);
            }
        });
    }
    getFloorByIndex(index) {
        return this.floors.find(floor => floor.index === index);
    }
    getFloorIndexByIndex(index) {
        return this.floors.findIndex(floor => floor.index === index);
    }
    recalculateRoute() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.currentlyRouting) {
            return;
        }
        const currentPos = turf.point([
            ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
            ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
        ]);
        const distance = turf.pointToLineDistance(currentPos, this.currentRoutingPath);
        const metersAway = distance * 1000;
        this.log('debug', `marker is ${metersAway} meters away from line`);
        if (metersAway > 20) {
            this.log('debug', 'recalculating route over 20m away');
            if (this.lastRoutingType === 'location') {
                this.routeToLocation(this.lastRoutingOptions, this.lastRoutingPadding);
            }
            if (this.lastRoutingType === 'amenity') {
                this.routeToAmenity(this.lastRoutingOptions, this.lastRoutingPadding);
            }
        }
    }
    arriveDestination() {
        var _a, _b, _c, _d, _e, _f;
        if (!this.currentlyRouting || !this.destinationCoords) {
            return;
        }
        const currentPos = turf.point([
            ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
            ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
        ]);
        const distance = turf.distance(currentPos, this.destinationCoords);
        const metersAway = distance * 1000;
        this.log('debug', `destination is ${metersAway} meters away from current location`);
        if (metersAway < 5) {
            this.emit('destinationArrived');
        }
    }
    displayRoute(paths, timeout, errorFn, paddingOptions) {
        if (!window.mapInMemory) {
            return;
        }
        if (paths.length === 1) {
            this.currentlyRouting = true;
            this.currentRoutingPath = turf.lineString(paths[0].Coordinates);
        }
        this.routingTimeouts.forEach(timeout => {
            if (timeout) {
                clearTimeout(timeout);
            }
        });
        this.routingTimeouts = [];
        this.routingLines = [];
        if (this.settings.disableAutoSwitchFloors) {
            timeout = 0;
        }
        paths.forEach((path, index) => {
            if (!window.mapInMemory) {
                return;
            }
            const uniqueId = uuid();
            const floor = this.getFloorByIndex(path.FloorIndex);
            const floorIndex = this.getFloorIndexByIndex(path.FloorIndex);
            if (!floor || floorIndex === -1) {
                return;
            }
            const lineName = `routing-line-${floor.index}-${uniqueId}`;
            const lineGeo = {
                type: 'FeatureCollection',
                features: [
                    {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: [path.Coordinates[0]]
                        },
                        properties: {}
                    }
                ]
            };
            try {
                const lineSource = window.mapInMemory.getSource(lineName);
                if (lineSource) {
                    window.mapInMemory.removeSource(lineName).addSource(lineName, {
                        type: 'geojson',
                        data: lineGeo,
                        tolerance: 0.00001
                    });
                }
                else {
                    window.mapInMemory.addSource(lineName, {
                        type: 'geojson',
                        data: lineGeo,
                        tolerance: 0.00001
                    });
                }
            }
            catch (e) {
                console.log('error adding routing line: ', lineName, e);
            }
            floor.layers.push(lineName);
            const newtimeout = setTimeout(() => {
                if (!window.mapInMemory) {
                    return;
                }
                const settings = this.lineLayerSettings({
                    name: lineName,
                    color: _.get(this.settings, 'venue.settings.mapboxSettings.navigationMainColor', '#cccccc'),
                    width: 8
                });
                try {
                    const lineSource = window.mapInMemory.getSource(lineName);
                    if (!lineSource) {
                        Sentry.captureException(new MapRoutingError(`source ${lineName} not found, recall routing`));
                        this.log('error', `source ${lineName} not found, recall routing`);
                        setTimeout(() => {
                            errorFn();
                        }, 100);
                    }
                    const layerExists = window.mapInMemory.getLayer(lineName);
                    if (layerExists) {
                        window.mapInMemory.removeLayer(lineName);
                    }
                    window.mapInMemory.addLayer(settings, `floor-${floor.index}`);
                    const first = path.Coordinates[0];
                    const last = path.Coordinates[path.Coordinates.length - 1];
                    const lineFeature = {
                        type: 'Feature',
                        geometry: {
                            type: 'LineString',
                            coordinates: path.Coordinates
                        },
                        properties: {}
                    };
                    const bbox = turf.bbox(lineFeature);
                    const options = {
                        easing: function (t) {
                            return t;
                        },
                        duration: 2000,
                        essential: true,
                        pitch: 0
                    };
                    if (paddingOptions) {
                        options.padding = paddingOptions;
                    }
                    else {
                        options.padding = { top: 50, bottom: 50, left: 50, right: 50 };
                    }
                    let bearing = this.getDefaultBearing(true);
                    if (this.settings.currentPosition) {
                        bearing = this.getRouteBearing(lineFeature);
                    }
                    const currentPitch = window.mapInMemory.getPitch();
                    if (currentPitch !== 0) {
                        const flyOptions = {
                            pitch: 0,
                            speed: 0.2,
                            essential: true
                        };
                        window.mapInMemory.flyTo(flyOptions);
                    }
                    const fitScreen = () => {
                        const currentPitch = window.mapInMemory.getPitch();
                        if (currentPitch !== 0) {
                            setTimeout(() => {
                                fitScreen();
                            }, 200);
                        }
                        else {
                            if (!this.settings.disableAutoSwitchFloors) {
                                const p0 = window.mapInMemory.project([bbox[0] - 0.0003, bbox[1] - 0.0003]);
                                const p1 = window.mapInMemory.project([bbox[2] + 0.0003, bbox[3] + 0.0003]);
                                this.log('debug', p0, p1, bearing, options);
                                window.mapInMemory.fitScreenCoordinates(p0, p1, bearing, options);
                                this.changeFloor({ newFloor: floor.floor, displayNotification: index !== 0 });
                                this.addAnimationToRoute(path, floor, lineName, lineGeo);
                                this.log('debug', 'Showing route for coords: ', first, last);
                            }
                            else {
                                this.routingLines.push({
                                    bbox,
                                    floor,
                                    path,
                                    lineName,
                                    lineGeo,
                                    bearing,
                                    options,
                                    firstRender: true,
                                });
                                if (index === 0) {
                                    this.changeFloor({ newFloor: floor.floor, displayNotification: true });
                                }
                            }
                        }
                    };
                    fitScreen();
                    if (index === paths.length - 1) {
                        setTimeout(() => {
                            this.emit('routeComplete');
                        }, 200);
                    }
                }
                catch (e) {
                    this.log('debug', 'error displaying route', e.message);
                }
            }, index * timeout + 100);
            this.routingTimeouts.push(newtimeout);
        });
    }
    easeNumbers(min, max, steps) {
        const numbers = [];
        const quadEasing = t => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t);
        const diff = max - min;
        for (let i = 0; i < steps; i += 1) {
            numbers.push(quadEasing(i / (steps - 1)) * diff + min);
        }
        return numbers;
    }
    addAnimationToRoute(path, floor, lineName, lineGeo) {
        if (!window.mapInMemory) {
            return;
        }
        const floorIndex = floor.index;
        const fIndex = this.getFloorIndexByIndex(floor.index);
        const navStepSize = _.get(this.settings, 'venue.settings.mapboxSettings.navigationStepSize', 0.0002);
        const stepDistance = parseFloat(navStepSize.toString());
        const line = turf.lineString(path.Coordinates);
        const lineDistance = turf.lineDistance(line, { units: 'kilometers' });
        const pointsOnLine = [];
        let steps = Math.floor(lineDistance / stepDistance);
        if (steps < 100)
            steps = 100;
        if (steps > 200)
            steps = 200;
        const easedDistances = this.easeNumbers(0, lineDistance, steps);
        easedDistances.forEach(distance => {
            const segment = turf.along(line, distance, { units: 'kilometers' });
            if (segment && segment.geometry) {
                pointsOnLine.push(segment.geometry.coordinates);
            }
        });
        const points = pointsOnLine.map(coord => {
            return turf.point(coord);
        });
        const lineFC = turf.featureCollection(points);
        path.Coordinates.forEach((coord, coordIndex) => {
            if (coordIndex === 0 || coordIndex === path.Coordinates.length) {
                return;
            }
            const point = turf.point(coord);
            const nearest = turf.nearestPoint(point, lineFC);
            pointsOnLine[nearest.properties.featureIndex] = coord;
        });
        const point = {
            type: 'Feature',
            properties: {},
            geometry: {
                type: 'Point',
                coordinates: path.Coordinates[0]
            }
        };
        const pointName = `point-${floorIndex}`;
        const pointLayer = window.mapInMemory.getLayer(pointName);
        if (pointLayer && this.floors[fIndex] !== undefined) {
            window.mapInMemory.removeLayer(pointName);
            if (this.floors[fIndex]) {
                this.floors[fIndex].layers = floor.layers.filter(name => name !== pointName);
            }
        }
        try {
            const pointSource = window.mapInMemory.getSource(pointName);
            if (pointSource) {
                window.mapInMemory.removeSource(pointName)
                    .addSource(pointName, {
                    type: 'geojson',
                    data: point,
                    tolerance: 0.00001
                })
                    .addLayer({
                    id: pointName,
                    source: pointName,
                    type: 'circle',
                    paint: {
                        'circle-radius': 10,
                        'circle-color': '#ffffff',
                        'circle-blur': 0.7,
                        'circle-opacity': 0.7
                    }
                });
            }
            else {
                window.mapInMemory
                    .addSource(pointName, {
                    type: 'geojson',
                    data: point,
                    tolerance: 0.00001
                })
                    .addLayer({
                    id: pointName,
                    source: pointName,
                    type: 'circle',
                    paint: {
                        'circle-radius': 10,
                        'circle-color': '#ffffff',
                        'circle-blur': 0.7,
                        'circle-opacity': 0.7
                    }
                });
            }
        }
        catch (e) {
            console.log('error adding routing point:', pointName, e);
        }
        this.animateRoute(point, 0, pointsOnLine, pointName, lineName, lineGeo, true);
        if (this.floors[fIndex] && this.floors[fIndex]) {
            this.floors[fIndex].layers.push(pointName);
        }
    }
    animateRoute(point, counter, pointsOnLine, layerName, lineName, lineGeo, firstTime) {
        if (!window.mapInMemory) {
            return;
        }
        if (pointsOnLine[counter]) {
            point.geometry.coordinates = pointsOnLine[counter];
            const animation = window.mapInMemory.getLayer(layerName);
            const layerSource = window.mapInMemory.getSource(layerName);
            try {
                if (firstTime) {
                    lineGeo.features[0].geometry.coordinates.push(pointsOnLine[counter]);
                    window.mapInMemory.getSource(lineName).setData(lineGeo);
                }
                if (layerSource && animation) {
                    layerSource.setData(point);
                }
                else {
                    this.log('debug', 'animation or layersource not set', animation, layerSource);
                }
            }
            catch (e) {
                this.log('debug', 'error Animate', e);
            }
            counter += 1;
            if (counter >= pointsOnLine.length) {
                counter = 0;
                firstTime = false;
            }
            requestAnimationFrame(() => {
                if (!window.mapInMemory) {
                    return;
                }
                const animation = window.mapInMemory.getLayer(layerName);
                if (animation) {
                    this.animateRoute(point, counter, pointsOnLine, layerName, lineName, lineGeo, firstTime);
                }
            });
        }
    }
    lineLayerSettings(options) {
        return {
            id: options.name,
            type: 'line',
            source: options.name,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                'line-color': options.color,
                'line-width': options.width
            }
        };
    }
    removeMarker(marker) {
        marker.mapboxPin.remove();
    }
    removeRoutes() {
        this.destinationCoords = undefined;
        this.currentlyRouting = false;
        this.log('debug', 'removeRoutes called');
        if (!window.mapInMemory) {
            return;
        }
        this.routingLines = [];
        this.routingTimeouts.forEach(timeout => {
            if (timeout) {
                clearTimeout(timeout);
            }
        });
        this.routingTimeouts = [];
        this.floors.forEach((floor, index) => {
            const search = 'routing-line';
            const search2 = 'point-';
            const layers = floor.layers.filter(layerName => layerName.includes(search) || layerName.includes(search2));
            layers.forEach(layerName => {
                if (!window.mapInMemory) {
                    return;
                }
                const layerExists = window.mapInMemory.getLayer(layerName);
                const sourceExists = window.mapInMemory.getSource(layerName);
                if (layerExists) {
                    window.mapInMemory.removeLayer(layerName);
                }
                if (sourceExists) {
                    window.mapInMemory.removeSource(layerName);
                }
            });
            if (this.floors[index] && this.floors[index].layers && floor.layers) {
                this.floors[index].layers = floor.layers.filter(layerName => !layerName.includes(search) && !layerName.includes(search2));
            }
        });
    }
    removeHighlights() {
        this.log('debug', 'removeHighlights called');
        if (!window.mapInMemory) {
            return;
        }
        const buildingHeight = _.get(this.settings, 'venue.settings.mapboxSettings.buildingHeight', 10);
        const normalHeight = parseFloat(buildingHeight.toString());
        const normalColor = _.get(this.settings, 'venue.settings.mapboxSettings.buildingTopColor', '#cccccc');
        this.floors.forEach(floor => {
            if (window.mapInMemory) {
                window.mapInMemory.setPaintProperty(`floor-${floor.index}`, 'fill-extrusion-color', [
                    'get',
                    'fillExtrusionColor'
                ]);
                window.mapInMemory.setPaintProperty(`floor-${floor.index}`, 'fill-extrusion-height', [
                    'get',
                    'fillExtrusionHeight'
                ]);
            }
        });
    }
    removeAllPins() {
        this.log('debug', 'removeAllPins called', window.mapPins);
        if (window.mapPins && window.mapPins.length > 0) {
            window.mapPins.forEach((pin) => {
                pin.remove();
            });
        }
        window.mapPins = [];
    }
    reset(opts) {
        this.log('debug', 'reset called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        this.removeRoutes();
        this.removeHighlights();
        this.showYouAreHereAccuracy();
        this.changeFloor({ newFloor: { id: this.getDefaultFloorId() } });
        window.mapInMemory.stop();
        const flyOptions = {
            center: this.getMapCenter(true),
            zoom: this.getDefaultZoom(),
            bearing: this.getDefaultBearing(true),
            pitch: this.getDefaultPitch(),
            speed: 0.4,
            essential: true
        };
        if (opts.padding) {
            flyOptions.padding = opts.padding;
        }
        window.mapInMemory.flyTo(flyOptions);
        this.clearPins();
        this.displayDefaultAmenities();
        opts.notify();
    }
    coordinateToLngLat(coord) {
        return new mapboxgl.LngLat(parseFloat(coord.lng), parseFloat(coord.lat));
    }
    flyin(opts) {
        this.log('debug', 'flyin called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        window.mapInMemory.stop();
        const defaultZoom = this.getDefaultZoom();
        const zoomChange = opts.zoomChange ? opts.zoomChange : 3;
        window.mapInMemory.jumpTo({
            zoom: defaultZoom - zoomChange
        });
        const options = {
            center: opts.coordinates ? this.coordinateToLngLat(opts.coordinates) : this.getMapCenter(true),
            zoom: this.getDefaultZoom(),
            bearing: opts.bearing ? opts.bearing : this.getDefaultBearing(true),
            pitch: opts.pitch ? opts.pitch : this.getDefaultPitch(),
            speed: opts.speed ? opts.speed : 0.4,
            essential: true
        };
        if (opts.padding) {
            options.padding = opts.padding;
        }
        window.mapInMemory.flyTo(options);
    }
    animate(opts) {
        this.log('debug', 'animate called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        const flyToConfig = {
            speed: opts.duration,
            essential: true
        };
        if (opts.moveTo) {
            flyToConfig.center = [parseFloat(opts.moveTo.lng), parseFloat(opts.moveTo.lat)];
        }
        if (opts.zoom) {
            flyToConfig.zoom = opts.zoom;
        }
        if (opts.rotation) {
            flyToConfig.bearing = opts.rotation;
        }
        if (opts.pitch) {
            flyToConfig.pitch = opts.pitch;
        }
        if (opts.padding) {
            flyToConfig.padding = opts.padding;
        }
        window.mapInMemory.flyTo(flyToConfig);
    }
    getAmenityTypePin(amenityTypeId) {
        let customPin = '';
        if (this.settings.features) {
            const amenityTypeFeature = this.settings.features
                .filter(feature => feature.feature === 'amenity_types')
                .find(feature => feature.settings.amenity_type === amenityTypeId);
            if (amenityTypeFeature && amenityTypeFeature.settings && amenityTypeFeature.settings.pinImage) {
                customPin = amenityTypeFeature.settings.pinImage;
            }
        }
        const newCustomPin = _.get(this.settings, `venue.settings.amenitySettings.customPins.${amenityTypeId}`);
        if (newCustomPin) {
            customPin = newCustomPin;
        }
        return customPin;
    }
    getAmenityPin(amenity, customPin) {
        const el = document.createElement('div');
        el.className = 'amenityMarker';
        if (customPin !== '') {
            el.innerHTML = `<div style="background-image: url('/static/pin.png'); background-repeat: no-repeat; background-position: bottom; background-size: 100% auto; width: 4.44vw; height: 5.74vw;">
        <img class="amentity-custom-pin" style="width: 4.44vw; height: 4.44vw;" src="${customPin}"/>
        </div>`;
        }
        else {
            el.innerHTML = '<i class="material-icons icon">place</i>';
        }
        el.addEventListener('click', e => {
            this.routeToAmenity({ amenity });
            e.stopPropagation();
        });
        return el;
    }
    getAmenityByTypeAndFloor(amenityTypeId, floorId) {
        let searchAmenities = _get(this.settings, 'amenities', []);
        if (this.floors.length > 1) {
            searchAmenities = searchAmenities.filter(amenity => amenity.settings.mapboxFloor === floorId);
        }
        const amenities = searchAmenities.filter(amenity => {
            return amenity.amenity_types.some(type => type.id === amenityTypeId);
        });
        return amenities;
    }
    showAmenityType(amenityTypeId) {
        this.log('debug', 'showAmenityType called with amenityTypeId: ', amenityTypeId);
        if (this.currentFloorId) {
            const amenities = this.getAmenityByTypeAndFloor(amenityTypeId, this.currentFloorId);
            const customPin = this.getAmenityTypePin(amenityTypeId);
            amenities.forEach(amenity => {
                const el = this.getAmenityPin(amenity, customPin);
                this.setMarker({
                    html: '',
                    htmlElement: el,
                    coord: { lng: amenity.longitude, lat: amenity.latitude }
                });
            });
        }
    }
    displayDefaultAmenities() {
        const amenitySettings = _.get(this.settings, 'venue.settings.amenitySettings', {
            showAll: false,
            showSome: false,
            showTypes: []
        });
        if (amenitySettings.showAll || amenitySettings.showSome) {
            amenitySettings.showTypes.forEach(amenityTypeId => {
                this.showAmenityType(amenityTypeId);
            });
        }
    }
    rotate(opts) {
        this.log('debug', 'rotate called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        const options = {
            bearing: opts.rotation,
            speed: opts.duration
        };
        if (opts.padding) {
            options.padding = opts.padding;
        }
        window.mapInMemory.flyTo(options);
    }
    gimbal(opts) {
        this.log('debug', 'gimbal called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        if (opts.useDiff) {
            const currentBearing = window.mapInMemory.getBearing();
            const currentPitch = window.mapInMemory.getPitch();
            if (opts.bearing !== 0) {
                window.mapInMemory.setBearing(currentBearing + opts.bearing * (180 / Math.PI));
            }
            if (opts.pitch !== 0) {
                window.mapInMemory.setPitch(currentPitch + opts.pitch * -1 * (180 / Math.PI));
            }
        }
        else {
            window.mapInMemory.setBearing(opts.bearing);
            window.mapInMemory.setPitch(opts.pitch);
        }
    }
    routeToAmenityPath(paths, opts, paddingOptions) {
        const lat = opts.amenity.latitude.toString();
        const lng = opts.amenity.longitude.toString();
        if (paths.length === 0) {
            this.flyin({
                coordinates: { lat, lng },
                padding: paddingOptions
            });
            this.log('debug', 'No route to amenity');
            this.emit('noRoute', `No route found to amenity ${opts.amenity.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}`);
            return;
        }
        this.lastRoutingType = 'amenity';
        this.lastRoutingOptions = opts;
        this.lastRoutingPadding = paddingOptions;
        if (!opts.attemptNumber) {
            opts.attemptNumber = 1;
        }
        this.displayRoute(paths, 5000, () => {
            opts.attemptNumber += 1;
            if (opts.attemptNumber <= 5) {
                this.routeToAmenity(opts, paddingOptions);
            }
            else {
                Sentry.captureException(new MapRoutingError(`giving up trying to route to amenity after 5 attempts`));
                this.log('error', `giving up trying to route to amenity after 5 attempts`, opts);
            }
        }, paddingOptions);
        setTimeout(() => {
            this.setMarker({
                coord: { lat, lng },
                html: '',
                htmlElement: this.getAmenityPin(opts.amenity, this.getAmenityTypePin(opts.amenity.amenity_types[0].id))
            });
        }, 5000 * (paths.length - 1) + 110);
    }
    routeToAmenity(opts, paddingOptions) {
        var _a, _b;
        if ((_a = this.browserSettings) === null || _a === void 0 ? void 0 : _a.disableClickRouting) {
            this.log('debug', 'exiting routeToAmenity because browserSettings.disableClickRouting is truthy', this.browserSettings);
            return;
        }
        this.log('debug', 'routeToAmenity called with opts: ', opts);
        this.removeRoutes();
        this.removeHighlights();
        try {
            const lat = opts.amenity.latitude.toString();
            const lng = opts.amenity.longitude.toString();
            if (this.settings.useRoutingLibrary) {
                this.log('debug', 'using routing library');
                const start = this.getStartingCoordinate();
                const floorId = ((_b = opts.amenity.settings) === null || _b === void 0 ? void 0 : _b.mapboxFloor) || this.getDefaultFloorId();
                const amenityFloor = this.floors.find((floor) => floor.floor.id === floorId);
                const startPos = {
                    floorIndex: start.floorIndex,
                    coordinate: start.coordinates,
                };
                const endPos = {
                    floorIndex: amenityFloor.floor.index,
                    coordinate: [parseFloat(lng), parseFloat(lat)],
                };
                const paths = this.mapboxRouter.CoordinateToCoordinate(startPos, endPos, opts.handicap);
                this.routeToAmenityPath(paths, opts, paddingOptions);
            }
            else {
                this.log('debug', 'using precalculated route');
                this.getPrecalculatedRoute('amenity_id', opts.amenity.id, opts.handicap).then(paths => {
                    this.routeToAmenityPath(paths, opts, paddingOptions);
                });
            }
        }
        catch (e) {
            this.emit('noRoute', `Error routing to amenity ${opts.amenity.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}. Error: ${e.message}`);
            this.log('debug', 'Error routing to amenity', opts, e);
        }
    }
    getStartingCoordinate() {
        var _a, _b, _c, _d, _e, _f;
        const defaultFloorId = this.getDefaultFloorId();
        const found = this.floors.find((floor) => {
            return floor.floor.id === defaultFloorId;
        });
        const startingIndex = found.index;
        let startCoords;
        if (this.settings.currentPosition) {
            startCoords = [
                ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
                ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
            ];
        }
        else {
            startCoords = this.getKioskLocation();
        }
        return {
            coordinates: startCoords,
            floorIndex: startingIndex,
        };
    }
    routeToLocationPaths(paths, opts, paddingOptions) {
        if (paths.length > 0) {
            this.lastRoutingType = 'location';
            this.lastRoutingOptions = opts;
            this.lastRoutingPadding = paddingOptions;
            if (!opts.attemptNumber) {
                opts.attemptNumber = 1;
            }
            this.displayRoute(paths, 5000, () => {
                opts.attemptNumber += 1;
                if (opts.attemptNumber <= 5) {
                    this.routeToLocation(opts, paddingOptions);
                }
                else {
                    Sentry.captureException(new MapRoutingError(`giving up trying to route to location after 5 attempts`));
                    this.log('error', `giving up trying to route to location after 5 attempts`, opts);
                }
            }, paddingOptions);
            if (this.browserSettings.isMobile) {
                this.hideYouAreHereAccuracy();
            }
        }
        else {
            this.flyInToLocation(opts.location.id, 0.0005, paddingOptions);
            this.emit('noRoute', `No route found to location ${opts.location.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}`);
        }
    }
    routeToLocation(opts, paddingOptions) {
        this.log('debug', 'routeToLocation called with opts: ', opts);
        this.removeRoutes();
        try {
            try {
                if (this.settings.useRoutingLibrary) {
                    this.log('debug', 'using routing library');
                    const start = this.getStartingCoordinate();
                    const paths = this.mapboxRouter.CoordinateToLocation({
                        floorIndex: start.floorIndex,
                        coordinate: start.coordinates,
                    }, opts.location.id, opts.handicap);
                    this.routeToLocationPaths(paths, opts, paddingOptions);
                }
                else {
                    this.log('debug', 'using precalculated route');
                    this.getPrecalculatedRoute('location_id', opts.location.id, opts.handicap).then((paths) => {
                        this.routeToLocationPaths(paths, opts, paddingOptions);
                    });
                }
            }
            catch (e) {
                this.log('error', 'error routing to location', e);
            }
        }
        catch (e) {
            this.emit('noRoute', `Error routing to location ${opts.location.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}. Error: ${e.message}.`);
            this.log('debug', 'Error routing to location', opts, e);
        }
    }
    getAmenityFloorPosition(amenityId) {
        const found = this.settings.amenities.find((amenity) => amenity.id === amenityId);
        if (!found) {
            return;
        }
        const floor = this.floors.find((floor) => floor.floor.id === found.settings.mapboxFloor);
        if (!floor) {
            return;
        }
        return {
            floorIndex: floor.index,
            coordinate: [parseFloat(found.longitude), parseFloat(found.latitude)]
        };
    }
    routeAnyToAny(opts, paddingOptions) {
        if (!this.settings.useRoutingLibrary) {
            throw 'must be using routing library to use this feature';
        }
        let start = { floorIndex: 0, coordinate: [0, 0] };
        let end = { floorIndex: 0, coordinate: [0, 0] };
        if (opts.start.type === 'location') {
            start = this.mapboxRouter.getLocationPosition(opts.start.id);
        }
        else if (opts.start.type === 'amenity') {
            start = this.getAmenityFloorPosition(opts.start.id);
        }
        if (opts.end.type === 'location') {
            end = this.mapboxRouter.getLocationPosition(opts.end.id);
        }
        else if (opts.end.type === 'amenity') {
            end = this.getAmenityFloorPosition(opts.end.id);
        }
        if (!start || !end) {
            this.emit('noRoute', `Error routing to ${opts.start.type} ${opts.start.id} to ${opts.end.type} ${opts.end.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}. Error: invalid start or end.`);
            this.log('debug', 'Error routing bad start or end', opts, start, end);
            return;
        }
        this.log('debug', 'routeLocationToLocation called with opts: ', opts);
        this.removeRoutes();
        try {
            try {
                const paths = this.mapboxRouter.CoordinateToCoordinate(start, end, opts.handicap);
                if (paths.length > 0) {
                    if (!opts.attemptNumber) {
                        opts.attemptNumber = 1;
                    }
                    this.displayRoute(paths, 5000, () => {
                        opts.attemptNumber += 1;
                        if (opts.attemptNumber <= 5) {
                            this.routeAnyToAny(opts, paddingOptions);
                        }
                        else {
                            Sentry.captureException(new MapRoutingError(`giving up trying to route after 5 attempts`));
                            this.log('error', `giving up trying to route after 5 attempts`, opts);
                        }
                    }, paddingOptions);
                    if (this.browserSettings.isMobile) {
                        this.hideYouAreHereAccuracy();
                    }
                }
                else {
                    if (opts.start.id && opts.start.type === 'location') {
                        this.flyInToLocation(opts.start.id, 0.0005, paddingOptions);
                    }
                    this.emit('noRoute', `No route found to ${opts.start.type} ${opts.start.id} to ${opts.end.type} ${opts.end.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}`);
                }
            }
            catch (e) {
                this.log('error', 'error routing', e);
            }
        }
        catch (e) {
            this.emit('noRoute', `Error routing to ${opts.start.type} ${opts.start.id} to ${opts.end.type} ${opts.end.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}. Error: ${e.message}.`);
            this.log('debug', 'Error routing', opts, e);
        }
    }
    routeLocationToLocation(opts, paddingOptions) {
        if (!this.settings.useRoutingLibrary) {
            throw 'must be using routing library to use this feature';
        }
        this.log('debug', 'routeLocationToLocation called with opts: ', opts);
        this.removeRoutes();
        try {
            try {
                const paths = this.mapboxRouter.LocationToLocation(opts.startLocation.id, opts.endLocation.id, opts.handicap);
                if (paths.length > 0) {
                    if (!opts.attemptNumber) {
                        opts.attemptNumber = 1;
                    }
                    this.displayRoute(paths, 5000, () => {
                        opts.attemptNumber += 1;
                        if (opts.attemptNumber <= 5) {
                            this.routeLocationToLocation(opts, paddingOptions);
                        }
                        else {
                            Sentry.captureException(new MapRoutingError(`giving up trying to route to location after 5 attempts`));
                            this.log('error', `giving up trying to route to location after 5 attempts`, opts);
                        }
                    }, paddingOptions);
                    if (this.browserSettings.isMobile) {
                        this.hideYouAreHereAccuracy();
                    }
                }
                else {
                    this.flyInToLocation(opts.endLocation.id, 0.0005, paddingOptions);
                    this.emit('noRoute', `No route found to location ${opts.startLocation.id} to  ${opts.endLocation.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}`);
                }
            }
            catch (e) {
                this.log('error', 'error routing to location', e);
            }
        }
        catch (e) {
            this.emit('noRoute', `Error routing to location ${opts.startLocation.id} to  ${opts.endLocation.id}. Handicap mode was ${opts.handicap ? 'ENABLED' : 'DISABLED'}. Error: ${e.message}.`);
            this.log('debug', 'Error routing to location', opts, e);
        }
    }
    getPrecalculatedRoute(column, id, handicap) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.settings.currentPosition) {
                const insideGeofence = this.insideGeofence();
                if (!insideGeofence) {
                    this.log('error', 'Routing disabled while outside geofence');
                    return Promise.resolve([]);
                }
                this.log('debug', 'deviceRouting', this.browserSettings.deviceRouting);
                if (this.browserSettings.deviceRouting) {
                    return yield this.geoRoute(column, id, handicap);
                }
                let destinationType = 'location';
                if (column === 'amenity_id') {
                    destinationType = 'amenity';
                }
                this.emit('routeRequest');
                try {
                    const response = yield axios.get('https://api.roveiq.com/v1/routes/adhoc', {
                        params: {
                            venue_id: this.settings.venue.id,
                            start_floor_id: this.currentFloorId,
                            start: `${this.settings.currentPosition.coords.latitude},${this.settings.currentPosition.coords.longitude}`,
                            destination_id: id,
                            destination_type: destinationType,
                            handicap: this.handicapMode
                        }
                    });
                    if (response) {
                        this.emit('routeReceived');
                        const json = JSON.parse(response.data.data);
                        const json2 = JSON.parse(json);
                        const paths = json2.Paths;
                        if (paths.length > 0) {
                            const lastPath = paths[paths.length - 1];
                            this.destinationCoords = lastPath.Coordinates[lastPath.Coordinates.length - 1];
                            this.log('debug', `destination cords set to ${this.destinationCoords}`);
                        }
                        return Promise.resolve(paths);
                    }
                    this.log('error', 'Problem while pulling mobile route.');
                    return Promise.resolve([]);
                }
                catch (e) {
                    this.emit('routeReceived');
                    this.log('error', 'Problem while pulling mobile route.', e.message);
                    return Promise.resolve([]);
                }
            }
            let useHandicap = this.handicapMode;
            if (handicap !== undefined) {
                useHandicap = handicap;
            }
            const route = this.routes.find((route) => {
                const routeId = _.isString(route[column]) ? parseInt(route[column]) : route[column];
                const findId = parseInt(id.toString());
                return (routeId === findId && route.handicap === useHandicap);
            });
            if (!route || !route.paths) {
                this.log('error', 'No Route Found', column, id);
                return Promise.resolve([]);
            }
            return Promise.resolve(route.paths);
        });
    }
    locationHasRoute(opts) {
        let useHandicap = this.handicapMode;
        if (opts.handicap !== undefined) {
            useHandicap = opts.handicap;
        }
        const route = this.routes.find(r => r['location_id'] === opts.location.id && r.handicap === useHandicap);
        if (!route || !route.paths) {
            return Promise.resolve(false);
        }
        return Promise.resolve(true);
    }
    estimateTime(column, id) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            let doorLat, doorLng;
            try {
                if (column === 'amenity_id') {
                    const amenity = this.settings.amenities.find(amenity => amenity.id === id);
                    if (!amenity) {
                        this.log('error', 'error finding amenity', id);
                        return Promise.resolve(0);
                    }
                    doorLat = amenity.latitude;
                    doorLng = amenity.longitude;
                }
                else {
                    const location = this.findBuildingByLocationId(id);
                    if (!location) {
                        this.log('error', 'error finding location', id);
                        return Promise.resolve(0);
                    }
                    if (location.feature.properties.door) {
                        doorLat = location.feature.properties.door[1];
                        doorLng = location.feature.properties.door[0];
                    }
                    else if (location.feature.properties.door_x && location.feature.properties.door_y) {
                        doorLat = location.feature.properties.door_y;
                        doorLng = location.feature.properties.door_x;
                    }
                    else {
                        this.log('error', 'error finding getting door coords', id, location);
                        return Promise.resolve(0);
                    }
                    this.log('debug', 'found location door: ', doorLat, doorLng);
                }
                const startCoords = [
                    ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
                    ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
                ];
                if (startCoords[0] === 0 || startCoords[1] === 0) {
                    this.log('debug', 'current location not set');
                    return Promise.resolve(0);
                }
                const distance = turf.distance([doorLng, doorLat], startCoords);
                return Promise.resolve(distance * 1000);
            }
            catch (e) {
                this.log('error', 'error getting estimate time to a location: ', e);
                return Promise.resolve(0);
            }
        });
    }
    geoRoute(column, id, handicap) {
        var _a, _b, _c, _d, _e, _f;
        let doorLat, doorLng;
        try {
            if (column === 'amenity_id') {
                const amenity = this.settings.amenities.find(amenity => amenity.id === id);
                if (!amenity) {
                    this.log('error', 'error finding amenity', id);
                    return Promise.resolve([]);
                }
                doorLat = amenity.latitude;
                doorLng = amenity.longitude;
            }
            else {
                const location = this.findBuildingByLocationId(id);
                if (!location) {
                    this.log('error', 'error finding location', id);
                    return Promise.resolve([]);
                }
                if (location.feature.properties.door) {
                    doorLat = location.feature.properties.door[1];
                    doorLng = location.feature.properties.door[0];
                }
                else if (location.feature.properties.door_x && location.feature.properties.door_y) {
                    doorLat = location.feature.properties.door_y;
                    doorLng = location.feature.properties.door_x;
                }
                else {
                    this.log('error', 'error finding getting door coords', id, location);
                    return Promise.resolve([]);
                }
                this.log('debug', 'found location door: ', doorLat, doorLng);
            }
            const startCoords = [
                ((_c = (_b = (_a = this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.longitude) || 0,
                ((_f = (_e = (_d = this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.latitude) || 0
            ];
            const start = turf.point(startCoords);
            const floor = this.floors.find(floor => floor.floor.id === this.currentFloorId);
            if (!floor) {
                this.log('error', 'floor not found');
                return Promise.resolve([]);
            }
            const floorIndex = floor.index;
            const features = floor.navLines.map(line => {
                return line;
            });
            const distances = features.map(feature => {
                return turf.pointToLineDistance(start, feature);
            });
            const minDistance = _.min(distances);
            const minIndex = distances.findIndex(distance => distance === minDistance);
            const lineCords = features[minIndex].geometry.coordinates;
            const distance1 = turf.distance(startCoords, lineCords[0]);
            const distance2 = turf.distance(startCoords, lineCords[1]);
            let secondCoord = lineCords[0];
            if (distance2 < distance1) {
                secondCoord = lineCords[1];
            }
            const newLine = turf.lineString([startCoords, secondCoord]);
            features.push(newLine);
            const data = turf.featureCollection(features);
            const pathFinder = new Pathfinder(data, { tolerance: 0.00003 });
            const end = turf.point([doorLng, doorLat]);
            const geoPath = pathFinder.findPath(start, end);
            this.log('debug', 'navigating from: ', startCoords, ' to: ', end);
            if (!geoPath) {
                this.log('error', 'no route found', id);
                return Promise.resolve([]);
            }
            const path = {
                FloorIndex: floorIndex,
                Coordinates: geoPath.path
            };
            this.log('debug', 'path found:', path);
            return Promise.resolve([path]);
        }
        catch (e) {
            this.log('error', 'error while calculating route', e);
            return Promise.resolve([]);
        }
    }
    setMarker(opts) {
        this.log('debug', 'setMarker called with opts: ', opts);
        if (!window.mapInMemory || !opts.coord) {
            return;
        }
        let element;
        if (opts.htmlElement) {
            element = opts.htmlElement;
        }
        else {
            element = document.createElement('div');
            element.innerHTML = opts.html;
        }
        const anchor = opts.anchor || 'bottom';
        const pitchAlignment = opts.pitchAlignment || 'auto';
        const rotationAlignment = opts.rotationAlignment || 'auto';
        const newPin = new mapboxgl.Marker({ element, anchor, pitchAlignment, rotationAlignment })
            .setLngLat([Number(opts.coord.lng), Number(opts.coord.lat)])
            .addTo(window.mapInMemory);
        if (opts.youAreHere) {
            window.youAreHerePin = newPin;
        }
        else if (opts.youAreHereAccuracy) {
            window.youAreHerePinAccuracy = newPin;
        }
        else {
            window.mapPins.push(newPin);
        }
        return {
            id: uuid(),
            mapboxPin: newPin
        };
    }
    zoom(opts) {
        this.log('debug', 'zoom called with opts: ', opts);
        if (!window.mapInMemory) {
            return;
        }
        const options = {
            zoom: opts.zoom,
            speed: opts.duration
        };
        if (opts.padding) {
            options.padding = opts.padding;
        }
        window.mapInMemory.flyTo(options);
    }
    forceResize(fixedCorner) {
        this.log('debug', 'forceResize called with fixedCorner: ', fixedCorner);
        if (!window.mapInMemory) {
            return;
        }
        if (fixedCorner) {
            let newPoint;
            let fixedPoint;
            switch (fixedCorner) {
                case 'ne': {
                    fixedPoint = window.mapInMemory.getBounds().getNorthEast();
                    window.mapInMemory.resize();
                    newPoint = window.mapInMemory.getBounds().getNorthEast();
                    break;
                }
                case 'sw': {
                    fixedPoint = window.mapInMemory.getBounds().getSouthWest();
                    window.mapInMemory.resize();
                    newPoint = window.mapInMemory.getBounds().getSouthWest();
                    break;
                }
                case 'se': {
                    fixedPoint = window.mapInMemory.getBounds().getSouthEast();
                    window.mapInMemory.resize();
                    newPoint = window.mapInMemory.getBounds().getSouthEast();
                    break;
                }
                default: {
                    fixedPoint = window.mapInMemory.getBounds().getNorthWest();
                    window.mapInMemory.resize();
                    newPoint = window.mapInMemory.getBounds().getNorthWest();
                    break;
                }
            }
            const currentCenter = window.mapInMemory.getCenter();
            const shiftVector = {
                x: newPoint.lng - fixedPoint.lng,
                y: newPoint.lat - fixedPoint.lat
            };
            const newCenter = new mapboxgl.LngLat(currentCenter.lng - shiftVector.x, currentCenter.lat - shiftVector.y);
            window.mapInMemory.setCenter(newCenter);
        }
        else {
            window.mapInMemory.resize();
        }
    }
    clearPins() {
        this.log('debug', 'clearPins called');
        this.removeAllPins();
    }
    flyInToLocation(locationId, padding, paddingOptions) {
        if (!window.mapInMemory) {
            return;
        }
        this.log('debug', 'flyInToLocation called with locationId: ', locationId, ' padding: ', padding);
        window.mapInMemory.stop();
        const found = this.findBuildingByLocationId(locationId);
        if (!found || !found.feature) {
            this.log('debug', 'Location not found', locationId);
            return;
        }
        const floor = this.getFloorByIndex(found.floorIndex);
        if (!floor) {
            return;
        }
        this.changeFloor({ newFloor: floor.floor });
        const bbox = turf.bbox(found.feature.geometry);
        const p1 = [bbox[0] - padding, bbox[1] - padding];
        const p2 = [bbox[2] + padding, bbox[3] + padding];
        const options = {
            easing: function (t) {
                return t;
            },
            bearing: this.getDefaultBearing(true),
            essential: true
        };
        if (paddingOptions) {
            options.padding = paddingOptions;
        }
        window.mapInMemory.fitBounds([p1[0], p1[1], p2[0], p2[1]], options);
    }
    setPadding(opts) {
        if (!window.mapInMemory) {
            return;
        }
        const allOpts = {
            padding: opts,
            duration: 1000
        };
        window.mapInMemory.easeTo(allOpts);
    }
    getLocationDistance(locationId, type) {
        return __awaiter(this, void 0, void 0, function* () {
            const paths = yield this.getPrecalculatedRoute(type, locationId);
            this.log('debug', 'got paths', paths);
            if (paths.length > 0) {
                let totalDistance = 0;
                paths.forEach(path => {
                    for (let i = 1; i < path.Coordinates.length; i++) {
                        const dist = turf.distance(path.Coordinates[i - 1], path.Coordinates[i], { units: 'miles' });
                        totalDistance += dist;
                    }
                });
                this.log('debug', 'totalDistance (miles):', totalDistance);
                return Promise.resolve(totalDistance * NUM_FT_IN_MILE);
            }
            else {
                return Promise.resolve(0);
            }
        });
    }
    setHandicapMode(handicapMode) {
        this.handicapMode = handicapMode;
    }
    snapToLine() {
        var _a, _b, _c, _d, _e, _f;
        const updateCoords = {
            lat: ((_c = (_b = (_a = this === null || this === void 0 ? void 0 : this.settings) === null || _a === void 0 ? void 0 : _a.currentPosition) === null || _b === void 0 ? void 0 : _b.coords) === null || _c === void 0 ? void 0 : _c.latitude) || 0,
            lng: ((_f = (_e = (_d = this === null || this === void 0 ? void 0 : this.settings) === null || _d === void 0 ? void 0 : _d.currentPosition) === null || _e === void 0 ? void 0 : _e.coords) === null || _f === void 0 ? void 0 : _f.longitude) || 0
        };
        const currentPos = [updateCoords.lng, updateCoords.lat];
        if (this.currentRoutingPath) {
            const line = this.currentRoutingPath.geometry.coordinates;
            const lineString = turf.lineString(line);
            const closestPoint = turf.nearestPointOnLine(lineString, currentPos);
            if (closestPoint && closestPoint.geometry && closestPoint.geometry.coordinates) {
                const distance = turf.distance(closestPoint, currentPos) * 1000;
                const accuracy = _.get(this.settings, 'currentPosition.coords.accuracy', 100);
                if (distance < accuracy) {
                    this.log('debug', 'snapping to line');
                    updateCoords.lng = closestPoint.geometry.coordinates[0];
                    updateCoords.lat = closestPoint.geometry.coordinates[1];
                }
            }
        }
        return updateCoords;
    }
    isMobile() {
        return !!this.settings.currentPosition;
    }
    getIdString(id) {
        switch (true) {
            case typeof id === 'string':
                return id;
            case typeof id === 'number':
                return id.toString();
            case typeof id === 'object':
                return JSON.stringify(id);
            default:
                this.log('debug', 'mapload client/session id string:', id);
                return id;
        }
    }
    sendLoadReport() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield axios.post('https://api.roveiq.com/v1/mapLoads', this.mapLoadData);
                return Promise.resolve();
            }
            catch (e) {
                this.log('error', 'Could not send map load report', e);
                return Promise.resolve();
            }
        });
    }
    getLocationFloor(locationId) {
        const found = this.findBuildingByLocationId(locationId);
        if (!found || !found.feature) {
            this.log('debug', 'Location not found', locationId);
            return [-1];
        }
        return [found.floorIndex];
    }
}
//# sourceMappingURL=maps.js.map